/**
 * WebHotelier Pricing Bridge Service
 * Connects the pricing rules system with WebHotelier API
 * Handles rule application, price calculation, and rate synchronization
 */

import {
  getWebHotelierProxyService,
  registerResetHandler,
} from './webHotelierProxyService';
import { getWebHotelierRateUpdateService } from './webHotelierRateUpdateService';
import { pricingEngineService } from '../../pricing/services/pricingEngineService';
import { PropertyInfo } from '../../../types/webHotelier';
import {
  DynamicPricingConfig,
  PricingRule,
  ComparisonOperator,
  ChannelOverrideRule,
} from '../../../types/revenue';
import { evaluateCondition } from '../../pricing/services/pricingRulesCalculatorService';
import {
  handleDatabaseOperation,
  createSuccessResult,
  type ErrorHandlingResult,
} from '../../../utils/errorHandling';
import { supabase } from '../../../services/supabaseClient';
import { resolveFunctionsHttpError } from '../../../services/edgeFunctionErrors';
import {
  requireAuthenticatedUserId,
  getOwnedPropertyIds,
} from '../../../services/propertyAuthorization';
import { format, addDays, parseISO, differenceInDays } from 'date-fns';

// Mirrors ApplyRulesBodySchema.dateRange in
// supabase/functions/_shared/schemas/pricingEngine.ts — caught here so an
// oversized range fails with a readable message instead of a raw 400.
const MAX_APPLY_DATES = 730;
import {
  gapDetectionService,
  type ReservationGap,
} from '../../../services/gapDetectionService';
import {
  roomTypeLimitsService,
  resolveEffectiveLimit,
  type RoomTypeLimit,
} from '../../../services/roomTypeLimits';
import { PRICING_CONFIG } from '../../../config/pricingConfig';
import {
  normalizeMinLosOverrides,
  resolveEffectiveMinLos,
} from '../../../services/minLosResolver';
import {
  isExclusiveChannelOverride,
  selectPriceRules,
} from '../../../services/channelOverrideResolver';
import {
  describeBookingWindow,
  isWithinBookingWindow,
  resolveBookingWindowBounds,
} from '../../../services/bookingWindowResolver';

export interface RoomTypeMapping {
  localRoomTypeId: string;
  webHotelierRoomCode: string;
  webHotelierRateId?: number; // Numeric rate ID from WebHotelier (e.g., 849788)
  name: string;
  currentPrice: number;
  basePrice?: number;
  minPrice: number;
  maxPrice: number;
}

export interface PriceCalculationContext {
  propertyCode: string;
  roomTypeCode: string;
  date: string;
  currentPrice: number;
  occupancy: number;
  overallOccupancy: number;
  daysUntilArrival: number;
  seasonalFactor: number;
  competitorPrices?: number[];
}

export interface CalculatedPrice {
  originalPrice: number;
  suggestedPrice: number;
  adjustment: number;
  adjustmentPercentage: number;
  appliedRules: string[];
  confidence: number;
  rationale: string;
  minLos?: number; // Minimum length of stay restriction
  maxLos?: number; // Maximum length of stay restriction
}

export interface PricingExecutionResult {
  success: boolean;
  propertyCode: string;
  processedRooms: number;
  updatedRates: number;
  errors: Array<{
    roomType: string;
    date: string;
    error: string;
  }>;
  priceChanges: Array<{
    roomType: string;
    roomTypeCode?: string;
    date: string;
    oldPrice: number;
    newPrice: number;
    adjustment: number;
    appliedRules?: string[];
    occupancy?: number;
  }>;
}

export class WebHotelierPricingBridge {
  private webHotelierService: ReturnType<
    typeof getWebHotelierProxyService
  > | null = null; // Lazy initialization
  private rateUpdateService: ReturnType<
    typeof getWebHotelierRateUpdateService
  > | null = null; // Lazy initialization
  private roomTypeMappings = new Map<string, RoomTypeMapping[]>();
  private pricingConfigs = new Map<string, DynamicPricingConfig>();
  private lastSyncTime = new Map<string, Date>();
  private initializedProperties = new Set<string>(); // Track initialized properties
  private initializationPromises = new Map<
    string,
    Promise<ErrorHandlingResult<boolean>>
  >(); // Prevent concurrent initializations
  private static readonly INIT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get WebHotelier service with lazy initialization
   */
  private getWebHotelierService() {
    if (this.webHotelierService) {
      return this.webHotelierService;
    }

    try {
      console.log(
        '[WebHotelierPricingBridge] Lazy loading WebHotelier proxy service...'
      );
      this.webHotelierService = getWebHotelierProxyService();
    } catch (error) {
      console.error(
        '[WebHotelierPricingBridge] Failed to get WebHotelier proxy service:',
        error
      );
      this.webHotelierService = null;
    }
    return this.webHotelierService;
  }

  /**
   * Get rate update service with lazy initialization
   */
  private getRateUpdateService() {
    if (this.rateUpdateService) {
      return this.rateUpdateService;
    }

    try {
      console.log(
        '[WebHotelierPricingBridge] Lazy loading rate update service...'
      );
      this.rateUpdateService = getWebHotelierRateUpdateService();
    } catch (error) {
      console.error(
        '[WebHotelierPricingBridge] Failed to get rate update service:',
        error
      );
      this.rateUpdateService = null;
    }
    return this.rateUpdateService;
  }

  /**
   * Resolve property ID - converts WebHotelier code to database UUID if needed,
   * and verifies the authenticated user owns the resolved property.
   *
   * Security: never returns a property id that the current user is not assigned
   * to in user_properties. Prevents attackers from passing arbitrary property
   * codes/UUIDs to read pricing data, room mappings, or PMS configs for
   * properties they do not own.
   */
  private async resolvePropertyId(propertyIdOrCode: string): Promise<string> {
    const userId = await requireAuthenticatedUserId();
    const ownedIds = await getOwnedPropertyIds(userId);

    // Check if it's already a valid UUID format
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(propertyIdOrCode)) {
      if (!ownedIds.has(propertyIdOrCode)) {
        throw new Error(
          `Property "${propertyIdOrCode}" not found or you do not have access.`
        );
      }
      console.log(
        `[WebHotelierPricingBridge] ID ${propertyIdOrCode} is already a UUID (ownership verified)`
      );
      return propertyIdOrCode;
    }

    // Treat as WebHotelier code - look up database UUID
    console.log(
      `[WebHotelierPricingBridge] Resolving WebHotelier code ${propertyIdOrCode} to database UUID...`
    );

    // Try to find property by exact name match (WebHotelier code stored as name).
    // RLS on properties filters to rows the user has access to; we additionally
    // intersect with ownedIds in case a loose policy slips a row through.
    const { data: propertyByName } = await supabase
      .from('properties')
      .select('id')
      .eq('name', propertyIdOrCode)
      .maybeSingle();

    if (propertyByName && ownedIds.has(propertyByName.id)) {
      console.log(
        `[WebHotelierPricingBridge] Found property UUID by name: ${propertyByName.id}`
      );
      return propertyByName.id;
    }

    // Fall back to PMS integrations — scoped to properties the user owns
    // by filtering property_id through the ownedIds set.
    if (ownedIds.size > 0) {
      const { data: integrations } = await supabase
        .from('pms_integrations')
        .select('property_id, config')
        .eq('pms_type', 'webhotelier')
        .eq('active', true)
        .in('property_id', Array.from(ownedIds));

      if (integrations && integrations.length > 0) {
        for (const integration of integrations) {
          const config = integration.config as Record<string, unknown>;
          if (config?.['propertyCode'] === propertyIdOrCode) {
            console.log(
              `[WebHotelierPricingBridge] Found property UUID via PMS integration: ${integration.property_id}`
            );
            return integration.property_id;
          }
        }
      }
    }

    // Not found OR not owned — surface a single error that does not
    // distinguish between the two (avoids existence oracle).
    throw new Error(
      `Property "${propertyIdOrCode}" not found or you do not have access. Please visit the Properties page to sync this property from WebHotelier before running the pricing engine.`
    );
  }

  /**
   * Initialize the bridge for a property
   */
  async initializeProperty(
    propertyId: string
  ): Promise<ErrorHandlingResult<boolean>> {
    // Check if already initialized recently
    const lastSync = this.lastSyncTime.get(propertyId);
    if (
      lastSync &&
      Date.now() - lastSync.getTime() < WebHotelierPricingBridge.INIT_CACHE_TTL
    ) {
      if (this.initializedProperties.has(propertyId)) {
        // Already initialized and cache is fresh
        return createSuccessResult(true);
      }
    }

    // Check if initialization is already in progress
    const existingPromise = this.initializationPromises.get(propertyId);
    if (existingPromise) {
      return existingPromise;
    }

    // Start new initialization
    const initPromise = handleDatabaseOperation(async () => {
      // Resolve property ID to database UUID if needed
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);
      console.log(
        `[WebHotelierPricingBridge] Initializing bridge for property ${propertyId} (resolved: ${resolvedPropertyId})`
      );

      // Get WebHotelier property info
      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        throw new Error('WebHotelier service not available');
      }

      const propertyResult = await webHotelierService.getPropertyInfo();
      if (!propertyResult.success) {
        throw new Error(
          `Failed to get property info: ${propertyResult.error?.message}`
        );
      }

      const propertyInfo = propertyResult.data!;

      // Load room type mappings (use resolved UUID for database queries)
      await this.loadRoomTypeMappings(resolvedPropertyId, propertyInfo);

      // Fetch and attach WebHotelier rate IDs to mappings
      await this.loadWebHotelierRateIds(resolvedPropertyId);

      // Load pricing configuration (use resolved UUID for database queries)
      await this.loadPricingConfig(resolvedPropertyId);

      // Mark as initialized
      this.initializedProperties.add(propertyId);
      this.lastSyncTime.set(propertyId, new Date());

      console.log(
        `Initialized WebHotelier pricing bridge for property ${propertyId} (UUID: ${resolvedPropertyId})`
      );
      return true;
    }, `Initialize pricing bridge for property ${propertyId}`);

    // Store the promise and clean up when done
    this.initializationPromises.set(propertyId, initPromise);
    initPromise.finally(() => {
      this.initializationPromises.delete(propertyId);
    });

    return initPromise;
  }

  /**
   * Load room type mappings using configured default prices as the base
   * Falls back to calendar_data if no defaults are configured
   */
  private async loadRoomTypeMappings(
    propertyId: string,
    _propertyInfo: PropertyInfo
  ) {
    console.log('[PricingBridge] Loading room type mappings...');

    // First, fetch configured default prices from room_type_defaults table
    const { data: configuredDefaults, error: defaultsError } = await supabase
      .from('room_type_defaults')
      .select(
        'room_type_code, room_type_name, default_price, webhotelier_rate_id'
      )
      .eq('property_id', propertyId);

    // Build a map of configured defaults
    const defaultPricesMap = new Map<
      string,
      { name: string; price: number; rateId: number | null }
    >();
    if (!defaultsError && configuredDefaults && configuredDefaults.length > 0) {
      console.log(
        '[PricingBridge] Found configured default prices:',
        configuredDefaults
      );
      for (const d of configuredDefaults) {
        defaultPricesMap.set(d.room_type_code, {
          name: d.room_type_name || d.room_type_code,
          price: Number(d.default_price),
          rateId: d.webhotelier_rate_id,
        });
      }
    } else {
      console.log(
        '[PricingBridge] No configured defaults found, will use calendar_data prices'
      );
    }

    // Get today's date for fetching current rates from calendar
    const today = format(new Date(), 'yyyy-MM-dd');

    // Fetch from calendar_data table for room type info
    const { data: calendarData, error: calendarError } = await supabase
      .from('calendar_data')
      .select(
        'room_type_code, room_type_name, base_rate, final_rate, total_rooms'
      )
      .eq('property_id', propertyId)
      .eq('date', today);

    if (calendarError) {
      console.error(
        '[PricingBridge] Failed to load from calendar_data:',
        calendarError
      );
      // Fall back to loading from room_types table
      return this.loadRoomTypeMappingsFromRoomTypes(propertyId);
    }

    const mappings: RoomTypeMapping[] = [];
    const seenRoomTypes = new Set<string>();

    if (calendarData && calendarData.length > 0) {
      console.log('[PricingBridge] Found calendar_data:', {
        count: calendarData.length,
        roomTypes: calendarData.map((r) => r.room_type_code),
      });

      for (const row of calendarData) {
        const roomCode = row.room_type_code;
        if (roomCode && !seenRoomTypes.has(roomCode)) {
          seenRoomTypes.add(roomCode);

          // Use configured default price if available, otherwise use base_rate (original WebHotelier rate)
          // CRITICAL: Never use final_rate as base — it's already adjusted and causes compounding drift
          const configuredDefault = defaultPricesMap.get(roomCode);
          const basePrice =
            configuredDefault?.price || row.base_rate || row.final_rate || 0;
          const currentPrice = row.base_rate || row.final_rate || basePrice;

          mappings.push({
            localRoomTypeId: roomCode,
            webHotelierRoomCode: roomCode,
            name: configuredDefault?.name || row.room_type_name || roomCode,
            currentPrice: currentPrice,
            basePrice: basePrice, // Use configured default as base price
            minPrice: Math.round(basePrice * 0.7),
            maxPrice: Math.round(basePrice * 1.5),
            // Seed from the manually configured rate ID; loadWebHotelierRateIds()
            // overwrites this with a live match when one exists, but keeps this
            // fallback when the live /rates lookup finds no active parent rate.
            ...(configuredDefault?.rateId
              ? { webHotelierRateId: configuredDefault.rateId }
              : {}),
          });

          console.log('[PricingBridge] Added room mapping:', {
            roomCode,
            name: row.room_type_name,
            basePrice,
            currentPrice,
            usingConfiguredDefault: !!configuredDefault,
          });
        }
      }
    } else {
      console.log(
        '[PricingBridge] No calendar_data found for today, trying any date...'
      );

      // Try to get any date's data
      const { data: anyCalendarData, error: anyError } = await supabase
        .from('calendar_data')
        .select(
          'room_type_code, room_type_name, base_rate, final_rate, total_rooms'
        )
        .eq('property_id', propertyId)
        .limit(10);

      if (!anyError && anyCalendarData && anyCalendarData.length > 0) {
        for (const row of anyCalendarData) {
          const roomCode = row.room_type_code;
          if (roomCode && !seenRoomTypes.has(roomCode)) {
            seenRoomTypes.add(roomCode);

            // Use configured default price if available
            const configuredDefault = defaultPricesMap.get(roomCode);
            const basePrice =
              configuredDefault?.price || row.final_rate || row.base_rate || 0;

            mappings.push({
              localRoomTypeId: roomCode,
              webHotelierRoomCode: roomCode,
              name: configuredDefault?.name || row.room_type_name || roomCode,
              currentPrice: basePrice,
              basePrice: basePrice,
              minPrice: Math.round(basePrice * 0.7),
              maxPrice: Math.round(basePrice * 1.5),
              ...(configuredDefault?.rateId
                ? { webHotelierRateId: configuredDefault.rateId }
                : {}),
            });
          }
        }
      } else {
        // Fall back to room_types table
        return this.loadRoomTypeMappingsFromRoomTypes(propertyId);
      }
    }

    this.roomTypeMappings.set(propertyId, mappings);
    console.log(
      `[PricingBridge] Loaded ${mappings.length} room type mappings (using configured defaults where available)`
    );
  }

  /**
   * Fallback: Load room type mappings from room_types table
   */
  private async loadRoomTypeMappingsFromRoomTypes(propertyId: string) {
    console.log('[PricingBridge] Falling back to room_types table...');

    const { data: roomTypes, error } = await supabase
      .from('room_types')
      .select('*')
      .eq('property_id', propertyId);

    if (error) {
      console.error('[PricingBridge] Failed to load room_types:', error);
      this.roomTypeMappings.set(propertyId, []);
      return;
    }

    const mappings: RoomTypeMapping[] = [];

    if (roomTypes && roomTypes.length > 0) {
      for (const room of roomTypes) {
        const price = room.base_rate || room.base_price || 100;
        mappings.push({
          localRoomTypeId: room.id,
          webHotelierRoomCode: room.code || room.id,
          name: room.name,
          currentPrice: price,
          basePrice: price,
          minPrice: Math.round(price * 0.7),
          maxPrice: Math.round(price * 1.5),
        });
      }
    }

    this.roomTypeMappings.set(propertyId, mappings);
    console.log(
      `[PricingBridge] Loaded ${mappings.length} room type mappings from room_types`
    );
  }

  /**
   * Fetch WebHotelier rate IDs and attach them to room type mappings
   * The rate ID is needed for the /manage/rates API endpoint
   */
  private async loadWebHotelierRateIds(propertyId: string) {
    try {
      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        console.warn(
          '[PricingBridge] WebHotelier service not available for rate ID lookup'
        );
        return;
      }

      const ratesResult = await webHotelierService.getRates();
      if (!ratesResult.success || !ratesResult.data) {
        console.warn(
          '[PricingBridge] Failed to fetch rates from WebHotelier:',
          ratesResult.error
        );
        return;
      }

      const rates = ratesResult.data.rates || [];
      const mappings = this.roomTypeMappings.get(propertyId);
      if (!mappings || mappings.length === 0) {
        return;
      }

      // Map room type codes to rate IDs
      // IMPORTANT: WebHotelier only allows updating PARENT rates (parent=0)
      // Child rates (parent > 0) will cause "Pricing modifications on child rates not permitted" error
      for (const mapping of mappings) {
        // Only get PARENT rates (parent === 0) - child rates cannot be modified
        const parentRates = rates.filter(
          (r: { room: string; active: number; parent: number }) =>
            r.room === mapping.webHotelierRoomCode &&
            r.active === 1 &&
            r.parent === 0
        );

        if (parentRates.length > 0) {
          // Use the first parent rate found (typically "Non Refundable" or base rate)
          const selectedRate = parentRates[0];

          mapping.webHotelierRateId = selectedRate.id;
          console.log(
            `[PricingBridge] Mapped ${mapping.webHotelierRoomCode} to PARENT rate ID ${selectedRate.id} (${selectedRate.name})`
          );
        } else if (mapping.webHotelierRateId) {
          console.warn(
            `[PricingBridge] No active PARENT rate found live for ${mapping.webHotelierRoomCode}; ` +
              `falling back to configured rate ID ${mapping.webHotelierRateId} from room_type_defaults`
          );
        } else {
          console.warn(
            `[PricingBridge] No active PARENT rate found for room type ${mapping.webHotelierRoomCode}, ` +
              `and no configured rate ID fallback in room_type_defaults — price push will be skipped`
          );
        }
      }

      // Update the mappings
      this.roomTypeMappings.set(propertyId, mappings);
      console.log(
        '[PricingBridge] WebHotelier rate IDs loaded:',
        mappings.map((m) => ({
          code: m.webHotelierRoomCode,
          rateId: m.webHotelierRateId,
        }))
      );
    } catch (error) {
      console.error(
        '[PricingBridge] Error loading WebHotelier rate IDs:',
        error
      );
    }
  }

  /**
   * Load pricing configuration for property
   */
  private async loadPricingConfig(propertyId: string) {
    try {
      console.log(
        `[PricingBridge] Loading pricing rules for property: ${propertyId}`
      );

      // Try to load rules with the given propertyId
      let rules = await pricingEngineService.getPricingRules(propertyId);

      // If no rules found, try with property code (PHILORTEST)
      if (rules.length === 0) {
        console.log(
          `[PricingBridge] No rules found with UUID ${propertyId}, trying property code lookup`
        );
        const { data: property } = await supabase
          .from('properties')
          .select('code')
          .eq('id', propertyId)
          .single();

        if (property?.code) {
          console.log(
            `[PricingBridge] Trying with property code: ${property.code}`
          );
          rules = await pricingEngineService.getPricingRules(property.code);
        }
      }

      console.log(
        `[PricingBridge] Loaded ${rules.length} pricing rules for property ${propertyId}:`,
        rules.map((r) => ({
          id: r.id,
          name: r.name || r.rule_name,
          type: r.rule_type,
          active: r.active,
          scope: r.scope || 'global',
          room_type_ids: r.room_type_ids || [],
          conditions: r.conditions,
          actions: r.actions,
        }))
      );

      // Filter to only active rules
      const activeRules = rules.filter((r) => r.active);
      console.log(
        `[PricingBridge] Active rules: ${activeRules.length} of ${rules.length}`
      );

      // Convert rules to pricing config format
      const config: DynamicPricingConfig = {
        id: `config-${propertyId}`,
        propertyId,
        roomTypeId: 'all', // Apply to all room types
        // No hardcoded price bounds: limits come from room_type_limits per
        // room/date. A fixed 80–400 fallback RAISED cheap rooms to €80 and
        // capped expensive ones at €400 — behavior the edge function never had.
        occupancyThresholds:
          this.convertRulesToOccupancyThresholds(activeRules),
        bookingWindowRules: this.convertRulesToBookingWindowRules(activeRules),
        minLosRules: this.convertRulesToMinLosRules(activeRules),
        gapFillingRules: this.convertRulesToGapFillingRules(activeRules),
        channelOverrideRules:
          this.convertRulesToChannelOverrideRules(activeRules),
        active: true,
        lastUpdated: new Date(),
      };

      console.log(`[PricingBridge] Pricing config created:`, {
        occupancyRules: config.occupancyThresholds.length,
        bookingWindowRules: config.bookingWindowRules.length,
        minLosRules: config.minLosRules?.length || 0,
        gapFillingRules: config.gapFillingRules?.length || 0,
        channelOverrideRules: config.channelOverrideRules?.length || 0,
      });

      this.pricingConfigs.set(propertyId, config);
    } catch (error) {
      console.error(
        `Failed to load pricing config for property ${propertyId}:`,
        error
      );
    }
  }

  /**
   * Convert database pricing rules to occupancy thresholds
   */
  private convertRulesToOccupancyThresholds(rules: PricingRule[]) {
    console.log(
      `[PricingBridge] Converting occupancy rules. Input: ${rules.length} rules`
    );
    console.log(
      `[PricingBridge] Rule types:`,
      rules.map((r) => r.rule_type)
    );

    const filtered = rules.filter(
      (rule) =>
        rule.rule_type === 'high_occupancy' ||
        rule.rule_type === 'low_occupancy'
    );

    console.log(
      `[PricingBridge] After filter for high/low occupancy: ${filtered.length} rules`
    );

    const converted = filtered.map((rule) => {
      const threshold = {
        id: rule.id,
        name: rule.rule_name,
        // Defaults MUST mirror the edge function's evaluateRule: a
        // high_occupancy rule without a threshold always matches (>= 0), a
        // low_occupancy one never does with 'lt' vs 100 unless occupancy < 100.
        threshold:
          rule.conditions?.occupancy_threshold ??
          (rule.rule_type === 'high_occupancy' ? 0 : 100),
        operator:
          (rule.conditions?.operator as ComparisonOperator) ?? undefined,
        // Which occupancy value the threshold compares against — dropping this
        // silently degraded room_type-scoped rules to property-wide occupancy.
        occupancy_scope: rule.conditions?.occupancy_scope,
        // Resolved here (not raw) so the bridge gates on the same bounds as the
        // edge function: lead_time_* columns first, legacy conditions second.
        booking_window_days: resolveBookingWindowBounds(rule).max ?? undefined,
        booking_window_days_min:
          resolveBookingWindowBounds(rule).min ?? undefined,
        ruleType: rule.rule_type as 'high_occupancy' | 'low_occupancy',
        priceAdjustment: {
          // Preserve the configured type: 'amount' rules are flat € adjustments
          // and must not be re-interpreted as percentages.
          type:
            (rule.actions?.price_adjustment?.type as 'percentage' | 'amount') ??
            ('percentage' as const),
          value: rule.actions?.price_adjustment?.value || 0,
        },
        scope: rule.scope,
        room_type_ids: rule.room_type_ids,
        valid_from:
          rule.valid_from ||
          (rule.conditions?.date_range as { start_date?: string } | undefined)
            ?.start_date ||
          undefined,
        valid_until:
          rule.valid_until ||
          (rule.conditions?.date_range as { end_date?: string } | undefined)
            ?.end_date ||
          undefined,
      };
      console.log(`[PricingBridge] Converted occupancy rule:`, threshold);
      return threshold;
    });

    return converted;
  }

  /**
   * Convert database pricing rules to booking window rules
   */
  private convertRulesToBookingWindowRules(rules: PricingRule[]) {
    return rules
      .filter((rule) => rule.rule_type === 'booking_window')
      .map((rule) => ({
        id: rule.id,
        // lead_time_* columns win over the legacy conditions field; 14 only
        // applies when neither location holds a max.
        daysOut: resolveBookingWindowBounds(rule, 14).max ?? 14,
        daysOutMin: resolveBookingWindowBounds(rule).min ?? undefined,
        occupancyThreshold: rule.conditions.occupancy_threshold ?? 60,
        occupancyOperator:
          (rule.conditions.occupancy_operator as ComparisonOperator) ?? 'gte',
        occupancy_scope: rule.conditions.occupancy_scope,
        priceModification: {
          type:
            (rule.actions.price_adjustment?.type as 'percentage' | 'amount') ??
            ('percentage' as const),
          value: rule.actions.price_adjustment?.value || 0,
        },
        active: rule.active,
        scope: rule.scope,
        room_type_ids: rule.room_type_ids,
        // Dates live in conditions.date_range — top-level valid_from/valid_until
        // are stripped before save (see savePricingRule) and are always null.
        valid_from:
          rule.valid_from ||
          (rule.conditions.date_range as { start_date?: string } | undefined)
            ?.start_date ||
          undefined,
        valid_until:
          rule.valid_until ||
          (rule.conditions.date_range as { end_date?: string } | undefined)
            ?.end_date ||
          undefined,
      }));
  }

  /**
   * Convert database pricing rules to MinLOS rules
   */
  private convertRulesToMinLosRules(rules: PricingRule[]) {
    console.log(
      '[convertRulesToMinLosRules] Input rules:',
      rules.map((r) => ({ id: r.id, type: r.rule_type, name: r.rule_name }))
    );
    const filtered = rules.filter((rule) => rule.rule_type === 'min_los');
    console.log('[convertRulesToMinLosRules] After filter:', filtered.length);
    return filtered.map((rule) => {
      // Seasonal bounds are stored in conditions.date_range (both {start,end}
      // and {start_date,end_date} shapes exist in the wild). Surface them as
      // valid_from/valid_until so shouldApplyRule() gates the rule by date —
      // the edge function does the same via conditions.date_range, and the two
      // push sites (parent rates there, child rates here) must agree.
      const dr = rule.conditions.date_range as
        | {
            start?: string;
            end?: string;
            start_date?: string;
            end_date?: string;
          }
        | undefined;
      return {
        id: rule.id,
        name: rule.rule_name || rule.name,
        minLos: rule.conditions.min_los || 1,
        period: rule.conditions.period || 'daily',
        dayOfWeek: rule.conditions.day_of_week,
        dateRange: rule.conditions.date_range,
        allowGapFillOverride: rule.conditions.allow_gap_fill_override || false,
        ...normalizeMinLosOverrides(rule.conditions),
        active: rule.active,
        priority: rule.priority,
        scope: rule.scope,
        room_type_ids: rule.room_type_ids,
        valid_from: rule.valid_from || dr?.start_date || dr?.start || undefined,
        valid_until: rule.valid_until || dr?.end_date || dr?.end || undefined,
      };
    });
  }

  /**
   * Convert database pricing rules to Gap Filling rules
   */
  private convertRulesToGapFillingRules(rules: PricingRule[]) {
    console.log(
      '[convertRulesToGapFillingRules] Input rules:',
      rules.map((r) => ({ id: r.id, type: r.rule_type, name: r.rule_name }))
    );
    const filtered = rules.filter((rule) => rule.rule_type === 'gap_filling');
    console.log(
      '[convertRulesToGapFillingRules] After filter:',
      filtered.length
    );
    return filtered.map((rule) => {
      const newShape = rule.actions.price_adjustment as
        | { type?: 'percentage' | 'amount'; value?: number }
        | undefined;
      // Mirror the edge function's fallback chain: prefer the signed
      // price_adjustment, fall back to legacy discount_rate (always a
      // discount), else the -10% legacy default.
      const legacyDiscount = (
        rule.actions as { discount_rate?: number } | undefined
      )?.discount_rate;
      const priceAdjustment = {
        type: newShape?.type ?? ('percentage' as const),
        value:
          newShape?.value !== undefined
            ? newShape.value
            : legacyDiscount !== undefined
              ? -Math.abs(legacyDiscount)
              : -10,
      };
      const dr = rule.conditions.date_range as
        | {
            start?: string;
            end?: string;
            start_date?: string;
            end_date?: string;
          }
        | undefined;
      return {
        id: rule.id,
        name: rule.rule_name || rule.name,
        gapDuration: {
          min: rule.conditions.gap_min ?? 1,
          // No configured max means "any gap length" — the edge function does
          // not bound it either. The old `|| 3` default silently excluded
          // longer gaps only on this path.
          max: rule.conditions.gap_max ?? Number.MAX_SAFE_INTEGER,
        },
        priceAdjustment,
        // Cap on the adjustment magnitude, same default as the edge function.
        maxDiscount:
          (rule.actions as { max_discount?: number } | undefined)
            ?.max_discount || 25,
        conditions: {
          occupancyThreshold: rule.conditions.occupancy_threshold,
          bookingWindow: rule.conditions.booking_window,
        },
        active: rule.active,
        scope: rule.scope,
        room_type_ids: rule.room_type_ids,
        valid_from: rule.valid_from || dr?.start_date || dr?.start || undefined,
        valid_until: rule.valid_until || dr?.end_date || dr?.end || undefined,
      };
    });
  }

  /**
   * Convert database channel_override rules for the exclusive-gate-aware
   * price calculation. Keeps rule_type / priority / conditions.exclusive so
   * the converted shape satisfies channelOverrideResolver's PriceRuleLike and
   * the shared selectPriceRules gate is reused verbatim (no mirror drift).
   */
  private convertRulesToChannelOverrideRules(
    rules: PricingRule[]
  ): ChannelOverrideRule[] {
    return rules
      .filter((rule) => rule.rule_type === 'channel_override')
      .map((rule) => {
        const dr = rule.conditions?.date_range as
          | {
              start_date?: string;
              end_date?: string;
              start?: string;
              end?: string;
            }
          | undefined;
        return {
          id: rule.id,
          name: rule.rule_name,
          rule_type: 'channel_override' as const,
          priority: rule.priority,
          conditions: { exclusive: rule.conditions?.exclusive === true },
          adjustment: {
            type:
              (
                rule.actions?.price_adjustment as
                  | { type?: 'percentage' | 'amount' }
                  | undefined
              )?.type ?? ('percentage' as const),
            value:
              (rule.actions?.price_adjustment as { value?: number } | undefined)
                ?.value ?? 0,
          },
          active: rule.active,
          scope: rule.scope,
          room_type_ids: rule.room_type_ids,
          valid_from:
            rule.valid_from || dr?.start_date || dr?.start || undefined,
          valid_until: rule.valid_until || dr?.end_date || dr?.end || undefined,
        };
      });
  }

  /**
   * Check if a rule should apply to a specific room type and date
   */
  private shouldApplyRule(
    rule: {
      id?: string;
      name?: string;
      scope?: 'global' | 'room_type';
      room_type_ids?: string[];
      valid_from?: string;
      valid_until?: string;
    },
    roomTypeId: string,
    targetDate: Date
  ): boolean {
    const targetDateStr = format(targetDate, 'yyyy-MM-dd');

    // Check scope - if scope is 'room_type', verify the room type is in the list
    if (rule.scope === 'room_type') {
      if (!rule.room_type_ids || !rule.room_type_ids.includes(roomTypeId)) {
        console.log(
          `[PricingBridge] Rule "${rule.name || rule.id}" skipped for ${roomTypeId}: scope is 'room_type' but room not in list [${rule.room_type_ids?.join(', ') || 'empty'}]`
        );
        return false;
      }
      console.log(
        `[PricingBridge] Rule "${rule.name || rule.id}" applies to ${roomTypeId}: room is in room_type_ids list`
      );
    }
    // If scope is 'global' or undefined, it applies to all room types

    // Check date range — rule only applies within valid_from..valid_until
    if (rule.valid_from && targetDateStr < rule.valid_from) {
      console.log(
        `[PricingBridge] Rule "${rule.name || rule.id}" skipped for ${targetDateStr}: before valid_from ${rule.valid_from}`
      );
      return false;
    }

    if (rule.valid_until && targetDateStr > rule.valid_until) {
      console.log(
        `[PricingBridge] Rule "${rule.name || rule.id}" skipped for ${targetDateStr}: after valid_until ${rule.valid_until}`
      );
      return false;
    }

    return true;
  }

  /**
   * Calculate optimized prices for a date range
   */
  async calculatePrices(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    roomTypeId?: string
  ): Promise<ErrorHandlingResult<Map<string, CalculatedPrice>>> {
    return handleDatabaseOperation(async () => {
      // Resolve property ID to database UUID if needed
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      const results = new Map<string, CalculatedPrice>();
      const mappings = this.roomTypeMappings.get(resolvedPropertyId) || [];

      // Always refresh pricing config from DB to avoid the 5-min cache TTL
      // serving stale scope/room_type_ids when rules changed via SQL or another
      // browser tab. Cheap (single SELECT) compared to the full price-calc loop
      // below.
      await this.loadPricingConfig(resolvedPropertyId);
      const config = this.pricingConfigs.get(resolvedPropertyId);

      if (!config) {
        throw new Error(
          `No pricing configuration found for property ${propertyId} (UUID: ${resolvedPropertyId})`
        );
      }

      // Filter mappings if specific room type requested
      const targetMappings = roomTypeId
        ? mappings.filter((m) => m.localRoomTypeId === roomTypeId)
        : mappings;

      console.log(
        `[PricingBridge] calculatePrices for ${targetMappings.length} room types:`,
        targetMappings.map((m) => ({
          code: m.webHotelierRoomCode,
          name: m.name,
          price: m.currentPrice,
        }))
      );

      // OPTIMIZATION: Batch-load ALL calendar data in a single query instead of 1 query per date
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      const roomTypeCodes = targetMappings.map((m) => m.webHotelierRoomCode);

      // Load ALL room types for property (needed for property-wide gap filling and price limits)
      const { data: dbRoomTypes } = await supabase
        .from('room_types')
        .select('id, webhotelier_code')
        .eq('property_id', resolvedPropertyId)
        .not('webhotelier_code', 'is', null);

      const roomTypeIdToCode = new Map<string, string>(
        (dbRoomTypes || []).map((rt) => [rt.id, rt.webhotelier_code as string])
      );
      const roomCodeToId = new Map<string, string>(
        (dbRoomTypes || []).map((rt) => [rt.webhotelier_code as string, rt.id])
      );
      const allPropertyRoomCodes = (dbRoomTypes || []).map(
        (rt) => rt.webhotelier_code as string
      );

      // Load detected gaps whenever any rule needs them — either a
      // gap_filling rule (drops price on gap dates) or a min_los rule with
      // gap_override (relaxes minLOS on gap dates). Without this check the
      // min_los override silently no-ops when no gap_filling rule exists.
      // Always load ALL gaps for the property (no room type filter) — gaps are applied property-wide.
      const hasGapFillingRules =
        config.gapFillingRules && config.gapFillingRules.length > 0;
      const hasMinLosGapOverride = (config.minLosRules || []).some(
        (r) => r.active && (r.gapOverride?.enabled || r.allowGapFillOverride)
      );
      const needsGapData = hasGapFillingRules || hasMinLosGapOverride;
      const rawGapsMap = needsGapData
        ? await gapDetectionService.getGapsForDateRange(
            resolvedPropertyId,
            startDate,
            endDate
          )
        : new Map<string, ReservationGap>();

      // Two views of the same gaps, keyed by "roomCode-date":
      //   • detectedGapsMap (property-wide) — gap_filling DISCOUNTS drop the
      //     price on every room type for a low-demand date (intentional lever).
      //   • detectedGapsByRoom (per-room) — minLOS RELAXATION must only loosen
      //     the room type that actually has the orphan island; relaxing the
      //     minimum on rooms with open availability leaks revenue.
      // gapDetectionService uses "uuid-date" keys; we translate to room codes.
      const detectedGapsMap = new Map<string, ReservationGap>();
      const detectedGapsByRoom = new Map<string, ReservationGap>();
      if (rawGapsMap.size > 0 && roomTypeIdToCode.size > 0) {
        const gapsByDate = new Map<string, ReservationGap>();
        for (const [key, gap] of rawGapsMap) {
          // Key format: "<uuid 36 chars>-YYYY-MM-DD"
          const datePart = key.substring(37);
          if (!datePart) continue;
          // Per-room: anchor on the gap's OWN room type.
          const ownCode = roomTypeIdToCode.get(gap.roomTypeId);
          if (ownCode) {
            const existing = detectedGapsByRoom.get(`${ownCode}-${datePart}`);
            // Keep the SHORTEST gap on a date — most likely inside the
            // configured [min,max] window and consistent with lowest-wins.
            if (!existing || gap.duration < existing.duration) {
              detectedGapsByRoom.set(`${ownCode}-${datePart}`, gap);
            }
          }
          if (!gapsByDate.has(datePart)) gapsByDate.set(datePart, gap);
        }
        // Property-wide: every room type on each gap date (gap_filling only).
        for (const [datePart, gap] of gapsByDate) {
          for (const code of allPropertyRoomCodes) {
            detectedGapsMap.set(`${code}-${datePart}`, gap);
          }
        }
      }

      console.log(
        `[PricingBridge] Gap data: ${
          needsGapData
            ? `loaded ${detectedGapsMap.size} property-wide / ${detectedGapsByRoom.size} per-room entries (gap_filling=${hasGapFillingRules}, min_los_override=${hasMinLosGapOverride})`
            : 'skipped (no gap-aware rules)'
        }`
      );

      // Load calendar data for target room types (used for per-room-type occupancy & pricing).
      // MUST paginate: PostgREST silently caps a single response at ~1000 rows.
      // With 7 room types x 365 days (~2560 rows) the tail room types were dropped
      // entirely, so their per-date base_rate was missing and pricing fell back to
      // the flat per-room basePrice — corrupting those rooms' prices. Fetch every
      // page so the base anchor is complete.
      const PAGE_SIZE = 1000;
      const targetCalendarData: Array<{
        date: string;
        room_type_code: string;
        occupancy_percentage: number;
        booked_rooms: number;
        total_rooms: number;
        base_rate: number | null;
      }> = [];
      let calendarError: unknown = null;
      for (let offset = 0; ; offset += PAGE_SIZE) {
        const { data: page, error: pageError } = await supabase
          .from('calendar_data')
          .select(
            'date, room_type_code, occupancy_percentage, booked_rooms, total_rooms, base_rate'
          )
          .eq('property_id', resolvedPropertyId)
          .in('room_type_code', roomTypeCodes)
          .gte('date', startDateStr)
          .lte('date', endDateStr)
          .order('room_type_code', { ascending: true })
          .order('date', { ascending: true })
          .range(offset, offset + PAGE_SIZE - 1);
        if (pageError) {
          calendarError = pageError;
          break;
        }
        if (page && page.length > 0) {
          targetCalendarData.push(...page);
        }
        if (!page || page.length < PAGE_SIZE) break;
      }

      // Build lookup map for O(1) access: "roomTypeCode-date" -> calendarData
      const calendarDataMap = new Map<
        string,
        {
          occupancy_percentage: number;
          booked_rooms: number;
          total_rooms: number;
          base_rate: number | null;
        }
      >();
      if (!calendarError && targetCalendarData) {
        for (const row of targetCalendarData) {
          const key = `${row.room_type_code}-${row.date}`;
          calendarDataMap.set(key, row);
        }
      }
      console.log(
        `[PricingBridge] Batch-loaded ${calendarDataMap.size} calendar data records`
      );

      // Load ALL room types' calendar data for property-wide occupancy.
      // Paginated for the same reason as above — a single response caps at ~1000
      // rows, which would otherwise under-count total/booked rooms and skew the
      // property-wide occupancy used by property-scoped rules.
      const allPropertyCalendarData: Array<{
        date: string;
        booked_rooms: number;
        total_rooms: number;
      }> = [];
      for (let offset = 0; ; offset += PAGE_SIZE) {
        const { data: page } = await supabase
          .from('calendar_data')
          .select('date, booked_rooms, total_rooms')
          .eq('property_id', resolvedPropertyId)
          .gte('date', startDateStr)
          .lte('date', endDateStr)
          .order('room_type_code', { ascending: true })
          .order('date', { ascending: true })
          .range(offset, offset + PAGE_SIZE - 1);
        if (page && page.length > 0) {
          allPropertyCalendarData.push(...page);
        }
        if (!page || page.length < PAGE_SIZE) break;
      }

      // Compute property-wide occupancy per date from ALL room types
      const overallOccupancyMap = new Map<string, number>();
      if (allPropertyCalendarData) {
        const dateAgg = new Map<
          string,
          { totalRooms: number; bookedRooms: number }
        >();
        for (const row of allPropertyCalendarData) {
          const agg = dateAgg.get(row.date) || {
            totalRooms: 0,
            bookedRooms: 0,
          };
          agg.totalRooms += row.total_rooms || 0;
          agg.bookedRooms += row.booked_rooms || 0;
          dateAgg.set(row.date, agg);
        }
        for (const [d, agg] of dateAgg) {
          overallOccupancyMap.set(
            d,
            agg.totalRooms > 0
              ? Math.round((agg.bookedRooms / agg.totalRooms) * 100)
              : 0
          );
        }
      }

      // Batch-load ALL price limits (defaults + time-framed) for the property
      // These are resolved per-date to support seasonal overrides
      const allPriceLimits: RoomTypeLimit[] =
        await roomTypeLimitsService.getAllPropertyLimits(resolvedPropertyId);
      console.log(
        `[PricingBridge] Loaded ${allPriceLimits.length} price limits (defaults + time-framed)`
      );

      // Calculate prices for each room type and date (now using cached calendar data)
      for (const mapping of targetMappings) {
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
          const dateStr = format(currentDate, 'yyyy-MM-dd');
          const key = `${mapping.localRoomTypeId}-${dateStr}`;

          // Check if this date/room is in a detected gap.
          // detectedGap (property-wide) drives gap_filling discounts;
          // detectedGapForMinLos (per-room) drives minLOS relaxation.
          const gapKey = `${mapping.localRoomTypeId}-${dateStr}`;
          const detectedGap = detectedGapsMap.get(gapKey) || null;
          const detectedGapForMinLos = detectedGapsByRoom.get(gapKey) || null;

          // Get current context for price calculation (using pre-loaded calendar data)
          const context = this.buildPriceCalculationContextFromCache(
            mapping,
            currentDate,
            calendarDataMap,
            overallOccupancyMap
          );

          // Resolve effective price limits for this room type on this date
          // mapping.localRoomTypeId is a room code (e.g. "TRP"), but DB limits use UUIDs
          const roomTypeUuid =
            roomCodeToId.get(mapping.localRoomTypeId) ||
            mapping.localRoomTypeId;
          const roomLimits = resolveEffectiveLimit(
            allPriceLimits,
            roomTypeUuid,
            dateStr
          );
          const calculatedPrice = await this.calculateOptimalPrice(
            context,
            config,
            mapping.localRoomTypeId,
            currentDate,
            detectedGap,
            roomLimits,
            detectedGapForMinLos
          );
          results.set(key, calculatedPrice);

          currentDate = addDays(currentDate, 1);
        }
      }

      return results;
    }, `Calculate prices for property ${propertyId}`);
  }

  /**
   * Build context for price calculation using pre-loaded calendar data (no DB query)
   */
  private buildPriceCalculationContextFromCache(
    mapping: RoomTypeMapping,
    date: Date,
    calendarDataMap: Map<
      string,
      {
        occupancy_percentage: number;
        booked_rooms: number;
        total_rooms: number;
        base_rate: number | null;
      }
    >,
    overallOccupancyMap: Map<string, number>
  ): PriceCalculationContext {
    const dateStr = format(date, 'yyyy-MM-dd');
    const cacheKey = `${mapping.webHotelierRoomCode}-${dateStr}`;
    const calendarRow = calendarDataMap.get(cacheKey);

    // CRITICAL: anchor pricing to calendar_data.base_rate (the operator's
    // stable base, synced from WebHotelier), NOT the live channel rate. Using
    // the live rate (mapping.currentPrice) makes each repricing read back its
    // own previous output and diverge, drifting prices to wrong values over
    // repeated runs. Fall back to the configured default / live rate only when
    // no base_rate is stored yet.
    const currentPrice =
      calendarRow?.base_rate && calendarRow.base_rate > 0
        ? calendarRow.base_rate
        : mapping.basePrice || mapping.currentPrice;
    const occupancy = calendarRow?.occupancy_percentage || 0;
    const overallOccupancy = overallOccupancyMap.get(dateStr) ?? occupancy;

    const daysUntilArrival = Math.ceil(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    return {
      propertyCode: '',
      roomTypeCode: mapping.webHotelierRoomCode,
      date: dateStr,
      currentPrice,
      occupancy,
      overallOccupancy,
      daysUntilArrival: Math.max(0, daysUntilArrival),
      seasonalFactor: 1.0,
    };
  }

  /**
   * Build context for price calculation using calendar_data (same source as Calendar)
   */
  private async buildPriceCalculationContext(
    propertyId: string,
    mapping: RoomTypeMapping,
    date: Date
  ): Promise<PriceCalculationContext> {
    const resolvedPropertyId = await this.resolvePropertyId(propertyId);
    const dateStr = format(date, 'yyyy-MM-dd');

    // Get data from calendar_data table for occupancy info
    const { data: calendarRow, error } = await supabase
      .from('calendar_data')
      .select('*')
      .eq('property_id', resolvedPropertyId)
      .eq('room_type_code', mapping.webHotelierRoomCode)
      .eq('date', dateStr)
      .single();

    // Anchor pricing to calendar_data.base_rate (stable operator base) so rules
    // are applied on a fixed base and don't drift by reading back prior output.
    // Fall back to the configured default / live rate only when base_rate is
    // missing.
    const currentPrice =
      !error && calendarRow?.base_rate && calendarRow.base_rate > 0
        ? calendarRow.base_rate
        : mapping.basePrice || mapping.currentPrice;
    let occupancy = 0;

    if (!error && calendarRow) {
      // Get occupancy from calendar data
      occupancy = calendarRow.occupancy_percentage || 0;

      console.log(
        `[PricingBridge] Context for ${mapping.webHotelierRoomCode} on ${dateStr}:`,
        {
          configuredDefault: mapping.basePrice,
          currentPrice,
          occupancy,
          bookedRooms: calendarRow.booked_rooms,
          totalRooms: calendarRow.total_rooms,
        }
      );
    } else {
      console.log(
        `[PricingBridge] No calendar data for ${mapping.webHotelierRoomCode} on ${dateStr}, using configured default: €${currentPrice}`
      );
    }

    // Fetch overall property occupancy for this date
    const { data: allRoomData } = await supabase
      .from('calendar_data')
      .select('booked_rooms, total_rooms')
      .eq('property_id', resolvedPropertyId)
      .eq('date', dateStr);

    let overallOccupancy = occupancy;
    if (allRoomData && allRoomData.length > 0) {
      const totalRooms = allRoomData.reduce(
        (sum, r) => sum + (r.total_rooms || 0),
        0
      );
      const bookedRooms = allRoomData.reduce(
        (sum, r) => sum + (r.booked_rooms || 0),
        0
      );
      overallOccupancy =
        totalRooms > 0 ? Math.round((bookedRooms / totalRooms) * 100) : 0;
    }

    const daysUntilArrival = Math.ceil(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    // Get property code from properties table
    let propertyCode = '';
    const { data: property } = await supabase
      .from('properties')
      .select('code')
      .eq('id', resolvedPropertyId)
      .single();

    if (property?.code) {
      propertyCode = property.code;
    }

    return {
      propertyCode,
      roomTypeCode: mapping.webHotelierRoomCode,
      date: dateStr,
      currentPrice, // Now uses configured default as base
      occupancy,
      overallOccupancy,
      daysUntilArrival,
      seasonalFactor: this.calculateSeasonalFactor(date),
    };
  }

  /**
   * Calculate optimal price based on rules and context
   * @param detectedGap - If provided, indicates this date is within a detected gap between bookings
   */
  private async calculateOptimalPrice(
    context: PriceCalculationContext,
    config: DynamicPricingConfig,
    roomTypeId: string,
    targetDate: Date,
    detectedGap: ReservationGap | null = null,
    roomTypeLimits?: { minPrice: number; maxPrice: number },
    // Per-room gap for minLOS relaxation. Defaults to detectedGap for callers
    // that don't distinguish; the property-wide caller passes the per-room view.
    detectedGapForMinLos: ReservationGap | null = detectedGap
  ): Promise<CalculatedPrice> {
    let suggestedPrice = context.currentPrice;
    let totalAdjustment = 0;
    const appliedRules: string[] = [];
    let confidence = 0.8; // Base confidence

    // Exclusive channel_override gate — MUST agree with the edge function's
    // selectPriceRules call: overrides matching this date/room decide whether
    // the occupancy / booking-window / gap loops below run at all. MinLOS
    // rules, price limits and the €1 floor are never silenced.
    const matchingOverrides = (config.channelOverrideRules || []).filter(
      (rule) =>
        rule.active && this.shouldApplyRule(rule, roomTypeId, targetDate)
    );
    if (matchingOverrides.some(isExclusiveChannelOverride)) {
      console.log(
        `[PricingBridge] Exclusive channel override active for ${roomTypeId} on ${format(targetDate, 'yyyy-MM-dd')} — silencing other price rules`
      );
      config = {
        ...config,
        occupancyThresholds: [],
        bookingWindowRules: [],
        gapFillingRules: [],
      };
    }

    // Apply occupancy-based rules
    console.log(
      `[PricingBridge] Evaluating ${config.occupancyThresholds.length} occupancy rules for ${roomTypeId} on ${format(targetDate, 'yyyy-MM-dd')}, occupancy: ${context.occupancy}%`
    );
    console.log(
      `[PricingBridge] All occupancy thresholds:`,
      config.occupancyThresholds.map((t) => ({
        id: t.id,
        threshold: t.threshold,
        operator: t.operator,
        ruleType: (t as { ruleType?: string }).ruleType,
        scope: t.scope,
        room_type_ids: t.room_type_ids,
        adjustment: t.priceAdjustment,
      }))
    );

    for (const threshold of config.occupancyThresholds) {
      // Check scope and date range before applying rule
      if (!this.shouldApplyRule(threshold, roomTypeId, targetDate)) {
        console.log(
          `[PricingBridge] Occupancy rule skipped (scope/date check failed):`,
          {
            id: threshold.id,
            scope: threshold.scope,
            room_type_ids: threshold.room_type_ids,
            targetRoomTypeId: roomTypeId,
          }
        );
        continue;
      }

      // Determine operator - use explicit operator if set, otherwise infer from rule type
      const ruleType = (threshold as { ruleType?: string }).ruleType;
      let operator: ComparisonOperator = threshold.operator ?? 'gte'; // Default to gte

      // Backward compatibility: infer operator from rule type if not explicitly
      // set. Defaults MUST match the edge function ('lt' for low_occupancy).
      if (!threshold.operator) {
        if (ruleType === 'low_occupancy') {
          operator = 'lt';
        } else {
          operator = 'gte'; // high_occupancy and legacy rules default to >=
        }
      }

      // Resolve which occupancy to use based on rule's occupancy_scope
      const effectiveOccupancy =
        threshold.occupancy_scope === 'room_type'
          ? context.occupancy
          : context.overallOccupancy;

      // Optional rolling lead-time gate, same as the edge function.
      const withinBookingWindow = isWithinBookingWindow(
        context.daysUntilArrival,
        {
          min: threshold.booking_window_days_min,
          max: threshold.booking_window_days,
        }
      );

      const ruleApplies =
        withinBookingWindow &&
        evaluateCondition(effectiveOccupancy, operator, threshold.threshold);
      const operatorSymbol =
        operator === 'gt'
          ? '>'
          : operator === 'gte'
            ? '≥'
            : operator === 'lt'
              ? '<'
              : operator === 'lte'
                ? '≤'
                : '=';

      console.log(
        `[PricingBridge] Rule evaluation: id=${threshold.id}, ruleType=${ruleType}, explicitOperator=${threshold.operator}, usedOperator=${operator}, threshold=${threshold.threshold}%, occupancy=${effectiveOccupancy}% (scope=${threshold.occupancy_scope || 'property'}, roomType=${context.occupancy}%, overall=${context.overallOccupancy}%), result=${ruleApplies}`
      );

      if (ruleApplies) {
        console.log(
          `[PricingBridge] Occupancy rule MATCHES: ${effectiveOccupancy}% ${operatorSymbol} ${threshold.threshold}%`
        );
      } else {
        console.log(
          `[PricingBridge] Occupancy rule NOT applied: ${effectiveOccupancy}% ${operatorSymbol} ${threshold.threshold}% is false`
        );
      }

      if (ruleApplies) {
        const adjustment =
          threshold.priceAdjustment.type === 'percentage'
            ? (context.currentPrice * threshold.priceAdjustment.value) / 100
            : threshold.priceAdjustment.value;

        totalAdjustment += adjustment;
        const direction = adjustment >= 0 ? '+' : '';
        const ruleTypeLabel =
          ruleType === 'high_occupancy'
            ? 'High'
            : ruleType === 'low_occupancy'
              ? 'Low'
              : '';
        appliedRules.push(
          `${ruleTypeLabel} Occupancy ${effectiveOccupancy}% (${operatorSymbol} ${threshold.threshold}%) → ${direction}${threshold.priceAdjustment.value}${threshold.priceAdjustment.type === 'percentage' ? '%' : '€'}`
        );
        confidence += 0.1;
      }
    }

    // Apply booking window rules
    for (const rule of config.bookingWindowRules) {
      // Check scope and date range before applying rule
      if (!this.shouldApplyRule(rule, roomTypeId, targetDate)) {
        continue;
      }

      // Use occupancyOperator if set, default to 'gte' for backward compatibility
      const occupancyOperator = rule.occupancyOperator ?? 'gte';
      // Resolve occupancy based on rule's occupancy_scope
      const bwOccupancy =
        rule.occupancy_scope === 'room_type'
          ? context.occupancy
          : context.overallOccupancy;
      const occupancyConditionMet = evaluateCondition(
        bwOccupancy,
        occupancyOperator,
        rule.occupancyThreshold
      );

      const windowBounds = { min: rule.daysOutMin, max: rule.daysOut };

      if (
        rule.active &&
        isWithinBookingWindow(context.daysUntilArrival, windowBounds) &&
        occupancyConditionMet
      ) {
        const adjustment =
          rule.priceModification.type === 'percentage'
            ? (context.currentPrice * rule.priceModification.value) / 100
            : rule.priceModification.value;

        totalAdjustment += adjustment;
        appliedRules.push(
          `${describeBookingWindow(windowBounds)} booking window → +${rule.priceModification.value}${rule.priceModification.type === 'percentage' ? '%' : '€'}`
        );
        confidence += 0.05;
      }
    }

    // Apply MinLOS rules - track the highest minLos (governing rule), then
    // let the shared resolver relax it for orphan-gap / last-minute dates.
    let appliedMinLos: number | undefined;
    let governingMinLosRule: (typeof config.minLosRules)[number] | undefined;
    console.log(
      `[PricingBridge] Evaluating ${(config.minLosRules || []).length} MinLOS rules for ${roomTypeId} on ${format(targetDate, 'yyyy-MM-dd')}`
    );
    for (const rule of config.minLosRules || []) {
      // Check scope and date range before applying rule
      if (!this.shouldApplyRule(rule, roomTypeId, targetDate)) {
        console.log(
          `[PricingBridge] MinLOS rule ${rule.name} skipped (scope/date check failed)`
        );
        continue;
      }

      // Day-of-week gate (0=Sunday..6=Saturday) — mirrors the edge function's
      // conditions.day_of_week check so weekend-only rules don't leak into
      // every day of the week on this path.
      if (
        rule.dayOfWeek &&
        rule.dayOfWeek.length > 0 &&
        !rule.dayOfWeek.includes(targetDate.getDay())
      ) {
        console.log(
          `[PricingBridge] MinLOS rule ${rule.name} skipped (day-of-week ${targetDate.getDay()} not in [${rule.dayOfWeek.join(',')}])`
        );
        continue;
      }

      if (rule.active) {
        console.log(
          `[PricingBridge] MinLOS rule ${rule.name} APPLIED: Min ${rule.minLos} nights`
        );
        // Track the highest minLos requirement
        if (!appliedMinLos || rule.minLos > appliedMinLos) {
          appliedMinLos = rule.minLos;
          governingMinLosRule = rule;
        }
        appliedRules.push(
          `Min ${rule.minLos} night stay${rule.allowGapFillOverride ? ' (gap fill override allowed)' : ''}`
        );
      }
    }

    if (appliedMinLos !== undefined && governingMinLosRule) {
      const minLosResolution = resolveEffectiveMinLos({
        baseMinLos: appliedMinLos,
        gapOverride: governingMinLosRule.gapOverride,
        // Per-room gap only — relaxing minLOS on a room with open availability
        // (property-wide spread) would punch holes in the minimum-stay strategy.
        gapNights: detectedGapForMinLos?.duration,
        lastMinuteOverride: governingMinLosRule.lastMinuteOverride,
        daysAhead: context.daysUntilArrival,
      });
      if (minLosResolution.effectiveMinLos !== appliedMinLos) {
        appliedRules.push(
          `MinLOS relaxed ${appliedMinLos}→${minLosResolution.effectiveMinLos} (${minLosResolution.appliedOverride} override)`
        );
        appliedMinLos = minLosResolution.effectiveMinLos;
      }
    }

    // Apply Gap Filling rules - ONLY if this date is in a detected gap between bookings
    console.log(
      `[PricingBridge] Evaluating Gap Filling rules for ${roomTypeId} on ${format(targetDate, 'yyyy-MM-dd')}, detectedGap: ${detectedGap ? `YES (${detectedGap.duration}d gap)` : 'NO'}`
    );

    if (detectedGap) {
      // This date IS in a detected gap - find applicable gap filling rule
      for (const rule of config.gapFillingRules || []) {
        // Check scope and date range before applying rule
        if (!this.shouldApplyRule(rule, roomTypeId, targetDate)) {
          console.log(
            `[PricingBridge] Gap rule ${rule.name} skipped (scope/date check failed)`
          );
          continue;
        }

        // Check if rule applies to this gap duration
        const gapDurationMatches =
          detectedGap.duration >= rule.gapDuration.min &&
          detectedGap.duration <= rule.gapDuration.max;

        if (!gapDurationMatches) {
          console.log(
            `[PricingBridge] Gap rule ${rule.name} skipped: gap duration ${detectedGap.duration}d not in range ${rule.gapDuration.min}-${rule.gapDuration.max}d`
          );
          continue;
        }

        if (rule.active) {
          // Occupancy gate — mirrors the edge function: the rule is BLOCKED
          // when property-wide occupancy has already reached the threshold
          // (occupancy_scope defaults to 'property' there too).
          const occupancyConditionMet =
            rule.conditions?.occupancyThreshold == null ||
            context.overallOccupancy < rule.conditions.occupancyThreshold;

          // Check if booking window condition is met (if specified)
          const bookingWindowConditionMet =
            !rule.conditions?.bookingWindow ||
            context.daysUntilArrival <= rule.conditions.bookingWindow;

          if (occupancyConditionMet && bookingWindowConditionMet) {
            // Signed price adjustment: negative = discount, positive = premium.
            // Value/legacy fallbacks resolved at conversion time; cap the
            // magnitude at maxDiscount like the edge function does.
            const adjustmentValue = rule.priceAdjustment?.value ?? 0;
            const maxMagnitude = rule.maxDiscount ?? 25;
            const capped =
              Math.sign(adjustmentValue) *
              Math.min(Math.abs(adjustmentValue), maxMagnitude);
            const actualAdjustment =
              rule.priceAdjustment?.type === 'amount'
                ? capped
                : (context.currentPrice * capped) / 100;
            const sign = capped > 0 ? '+' : '';
            const unit = rule.priceAdjustment?.type === 'amount' ? '€' : '%';

            console.log(
              `[PricingBridge] Gap rule ${rule.name} APPLIED for ${detectedGap.duration}d gap: ${sign}${capped}${unit} (${actualAdjustment.toFixed(2)}€)`
            );
            totalAdjustment += actualAdjustment;
            appliedRules.push(
              `Gap filling (${detectedGap.duration}d gap) → ${sign}${capped}${unit}`
            );
            confidence += 0.1; // Higher confidence when applied to actual detected gap
            // No break: all matching gap rules stack additively, exactly like
            // the edge function's rule loop.
          } else {
            console.log(
              `[PricingBridge] Gap rule ${rule.name} conditions not met: overallOccupancy=${context.overallOccupancy}% (threshold=${rule.conditions?.occupancyThreshold}), daysOut=${context.daysUntilArrival} (window=${rule.conditions?.bookingWindow})`
            );
          }
        }
      }
    } else {
      console.log(
        `[PricingBridge] No detected gap for ${roomTypeId} on ${format(targetDate, 'yyyy-MM-dd')} - gap rules not applied`
      );
    }

    // Apply channel override rules (additive on the base price, computed from
    // context.currentPrice exactly like every other rule). When an exclusive
    // override matched, selectPriceRules returns ONLY the winner — and the
    // occupancy / booking-window / gap loops above were already silenced.
    for (const override of selectPriceRules(matchingOverrides)) {
      const overrideAdjustment =
        override.adjustment.type === 'percentage'
          ? (context.currentPrice * override.adjustment.value) / 100
          : override.adjustment.value;
      totalAdjustment += overrideAdjustment;
      const sign = override.adjustment.value > 0 ? '+' : '';
      const unit = override.adjustment.type === 'percentage' ? '%' : '€';
      console.log(
        `[PricingBridge] Channel override "${override.name || override.id}" APPLIED: ${sign}${override.adjustment.value}${unit} (${overrideAdjustment.toFixed(2)}€)${override.conditions.exclusive ? ' [exclusive]' : ''}`
      );
      appliedRules.push(
        `Channel override${override.conditions.exclusive ? ' (exclusive)' : ''} → ${sign}${override.adjustment.value}${unit}`
      );
      confidence += 0.05;
    }

    // Apply seasonal factor
    const seasonalAdjustment =
      context.currentPrice * (context.seasonalFactor - 1);
    totalAdjustment += seasonalAdjustment;
    if (Math.abs(seasonalAdjustment) > 1) {
      appliedRules.push(
        `Seasonal adjustment → ${seasonalAdjustment > 0 ? '+' : ''}${seasonalAdjustment.toFixed(2)}€`
      );
    }

    suggestedPrice = context.currentPrice + totalAdjustment;

    // Apply min/max price limits as a global safeguard
    // Room-type-specific limits from room_type_limits table take priority over config defaults
    const minPrice =
      roomTypeLimits?.minPrice ?? config.minPrice ?? config.priceRange?.min;
    const maxPrice =
      roomTypeLimits?.maxPrice ?? config.maxPrice ?? config.priceRange?.max;

    if (minPrice != null && suggestedPrice < minPrice) {
      suggestedPrice = minPrice;
      appliedRules.push(`Minimum price limit applied (${minPrice}€)`);
      confidence -= 0.1;
    }

    if (maxPrice != null && suggestedPrice > maxPrice) {
      suggestedPrice = maxPrice;
      appliedRules.push(`Maximum price limit applied (${maxPrice}€)`);
      confidence -= 0.1;
    }

    // Absolute floor — WebHotelier rejects zero/negative prices. Mirrors the
    // edge function's MINIMUM_PRICE_FLOOR (1.00€).
    if (suggestedPrice < 1) {
      suggestedPrice = 1;
      appliedRules.push('Minimum price floor applied (1€)');
    }

    // Round to reasonable precision
    suggestedPrice = Math.round(suggestedPrice * 100) / 100;
    confidence = Math.max(0.1, Math.min(1.0, confidence));

    const adjustmentPercentage =
      context.currentPrice > 0
        ? ((suggestedPrice - context.currentPrice) / context.currentPrice) * 100
        : 0;

    return {
      originalPrice: context.currentPrice,
      suggestedPrice,
      adjustment: suggestedPrice - context.currentPrice,
      adjustmentPercentage,
      appliedRules,
      confidence,
      rationale:
        appliedRules.length > 0
          ? appliedRules.join('; ')
          : 'No adjustments applied - maintaining current price',
      minLos: appliedMinLos,
    };
  }

  /**
   * Calculate seasonal factor based on date
   * DISABLED: Prices now only change based on explicit pricing rules
   */
  private calculateSeasonalFactor(_date: Date): number {
    // Seasonal factor disabled - prices only change based on explicit pricing rules
    // To re-enable seasonal pricing, uncomment below:
    // const month = _date.getMonth() + 1;
    // if (month >= 6 && month <= 8) return 1.2; // Summer premium (+20%)
    // if (month === 12 || month <= 2) return 1.1; // Winter holiday premium (+10%)
    // if (month === 4 || month === 5) return 1.05; // Spring premium (+5%)
    return 1.0; // No automatic seasonal adjustment
  }

  /**
   * Execute pricing strategy and push rates to WebHotelier
   */
  async executePricingStrategy(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    options: {
      dryRun?: boolean;
      roomTypeId?: string;
      minConfidence?: number;
      maxPriceChange?: number;
    } = {}
  ): Promise<ErrorHandlingResult<PricingExecutionResult>> {
    return handleDatabaseOperation(async () => {
      const {
        dryRun = false,
        minConfidence = 0.7,
        maxPriceChange = 2.0, // Allow up to 200% price changes for dynamic pricing rules
      } = options;

      // Resolve property ID to database UUID if needed
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      // Initialize if needed
      await this.initializeProperty(propertyId);

      // ALWAYS reload pricing config from DB before executing a strategy.
      // initializeProperty has a 5-minute cache TTL, so rules changed via SQL
      // or another tab/window can leak stale scope/room_type_ids into the
      // cached config — producing room-type-specific rules that incorrectly
      // fire property-wide. Reloading is cheap (single SELECT) and prevents
      // the silent cache-staleness bug.
      await this.loadPricingConfig(resolvedPropertyId);

      // Only run gap detection if gap_filling rules are configured
      const config = this.pricingConfigs.get(resolvedPropertyId);
      const hasGapFillingRules =
        config?.gapFillingRules && config.gapFillingRules.length > 0;

      if (hasGapFillingRules) {
        console.log(
          `[PricingBridge] Running gap detection for property ${resolvedPropertyId}`
        );
        const gapDetectionResult = await gapDetectionService.detectGaps(
          resolvedPropertyId,
          options.roomTypeId,
          {
            lookAheadDays: Math.ceil(
              (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
            ),
          }
        );
        if (gapDetectionResult.success) {
          console.log(
            `[PricingBridge] Gap detection found ${gapDetectionResult.gaps?.length || 0} gaps`
          );
        } else {
          console.warn(
            `[PricingBridge] Gap detection failed: ${gapDetectionResult.error}`
          );
        }
      } else {
        console.log(
          `[PricingBridge] Skipping gap detection - no gap_filling rules configured`
        );
      }

      // Calculate optimized prices (gap rules will use detected gaps)
      const calculationResult = await this.calculatePrices(
        propertyId,
        startDate,
        endDate,
        options.roomTypeId
      );

      if (!calculationResult.success) {
        throw new Error(
          `Price calculation failed: ${calculationResult.error?.message}`
        );
      }

      const calculatedPrices = calculationResult.data!;
      const mappings = this.roomTypeMappings.get(resolvedPropertyId) || [];

      console.log(`[PricingBridge] Strategy execution:`, {
        propertyId,
        resolvedPropertyId,
        mappingsCount: mappings.length,
        mappings: mappings.map((m) => ({
          code: m.webHotelierRoomCode,
          name: m.name,
          price: m.currentPrice,
        })),
        calculatedPricesCount: calculatedPrices.size,
      });

      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        throw new Error('WebHotelier service not available');
      }

      const propertyInfo = await webHotelierService.getPropertyInfo();
      const propertyCode = propertyInfo.data?.code || '';

      const result: PricingExecutionResult = {
        success: true,
        propertyCode,
        processedRooms: 0,
        updatedRates: 0,
        errors: [],
        priceChanges: [],
      };

      // Process each calculated price
      const rateUpdates: Array<{
        roomTypeCode: string;
        webHotelierRateId?: number;
        date: Date;
        calculatedPrice: number;
        originalPrice: number;
        confidence: number;
        minLos?: number;
      }> = [];

      for (const [key, calculatedPrice] of calculatedPrices) {
        // Key format: "roomTypeId-YYYY-MM-DD" - date is always 10 chars
        // Extract roomTypeId (everything before the last 11 chars) and date (last 10 chars)
        const dateStr = key.slice(-10); // YYYY-MM-DD
        const roomTypeId = key.slice(0, -11); // Remove "-YYYY-MM-DD"

        const mapping = mappings.find((m) => m.localRoomTypeId === roomTypeId);

        if (!mapping) {
          console.log(
            `[PricingBridge] No mapping found for roomTypeId: ${roomTypeId}`
          );
          continue;
        }

        result.processedRooms++;

        // Always add to priceChanges for preview (show all room types)
        result.priceChanges.push({
          roomType: mapping.name,
          roomTypeCode: mapping.webHotelierRoomCode,
          date: dateStr,
          oldPrice: calculatedPrice.originalPrice,
          newPrice: calculatedPrice.suggestedPrice,
          adjustment: calculatedPrice.adjustment,
          appliedRules: calculatedPrice.appliedRules,
          occupancy: calculatedPrice.confidence, // Include for debugging
        });

        // Only add to rateUpdates if confidence and change filters pass (for actual execution)
        if (calculatedPrice.confidence < minConfidence) {
          console.log(
            `[PricingBridge] Skipping ${mapping.name} on ${dateStr}: confidence ${calculatedPrice.confidence} < ${minConfidence}`
          );
          continue;
        }

        const changePercent =
          Math.abs(calculatedPrice.adjustmentPercentage) / 100;
        if (changePercent > maxPriceChange) {
          console.log(
            `[PricingBridge] Skipping ${mapping.name} on ${dateStr}: change ${(changePercent * 100).toFixed(1)}% > ${maxPriceChange * 100}%`
          );
          continue;
        }

        rateUpdates.push({
          roomTypeCode: mapping.webHotelierRoomCode,
          webHotelierRateId: mapping.webHotelierRateId, // Include rate ID for API
          date: parseISO(dateStr),
          calculatedPrice: calculatedPrice.suggestedPrice,
          originalPrice: calculatedPrice.originalPrice,
          confidence: calculatedPrice.confidence,
          minLos: calculatedPrice.minLos, // Include MinLOS restriction
        });
      }

      // Log summary of price changes by room type
      const roomTypeSummary = new Map<
        string,
        { count: number; totalAdjustment: number }
      >();
      for (const change of result.priceChanges) {
        const existing = roomTypeSummary.get(change.roomType) || {
          count: 0,
          totalAdjustment: 0,
        };
        roomTypeSummary.set(change.roomType, {
          count: existing.count + 1,
          totalAdjustment: existing.totalAdjustment + change.adjustment,
        });
      }

      console.log(
        `[PricingBridge] Price changes summary by room type:`,
        Object.fromEntries(roomTypeSummary)
      );

      console.log(
        `Executing pricing strategy: ${rateUpdates.length} rate updates for ${propertyCode}`
      );

      if (!dryRun) {
        // Recalculate on the server under the property lock. Never send client
        // preview prices: a CSV import may have changed the base meanwhile.
        //
        // The server recomputes, so it cannot honour the per-date minConfidence
        // and maxPriceChange filters itself. Instead we scope the apply to the
        // dates that PASSED those filters: a date whose preview breached the
        // guard is never handed to the engine, so it is left untouched. The
        // guard is approximate (the server's own number may differ slightly
        // from the preview) but it is the difference between a bounded apply
        // and an unbounded one.
        const guardedDates = [
          ...new Set(
            rateUpdates.map((update) => format(update.date, 'yyyy-MM-dd'))
          ),
        ].sort();

        if (guardedDates.length === 0) {
          // Every date was filtered out. Applying the full range here would
          // force-push exactly the prices the guards just rejected.
          console.warn(
            `[PricingBridge] No date passed the confidence/price-change guards for ${propertyCode}; skipping apply.`
          );
          result.success = true;
          result.updatedRates = 0;
        } else {
          const roomMapping = options.roomTypeId
            ? mappings.find(
                (m) =>
                  m.localRoomTypeId === options.roomTypeId ||
                  m.webHotelierRoomCode === options.roomTypeId
              )
            : undefined;
          if (options.roomTypeId && !roomMapping)
            throw new Error('Unknown room type for pricing.');
          if (guardedDates.length > MAX_APPLY_DATES)
            throw new Error(
              `Select a shorter period: pricing can be applied to at most ${MAX_APPLY_DATES} days at a time.`
            );
          const { data, error } = await supabase.functions.invoke(
            'pricing-engine/apply-rules',
            {
              body: {
                propertyId: resolvedPropertyId,
                trigger: 'manual_apply',
                dateRange: guardedDates,
                ...(roomMapping
                  ? { roomTypeCode: roomMapping.webHotelierRoomCode }
                  : {}),
              },
            }
          );
          if (error) throw await resolveFunctionsHttpError(error);
          result.success = data?.success === true;
          result.updatedRates = data?.webHotelierUpdated || 0;
          result.errors = (data?.errors || [data?.error].filter(Boolean)).map(
            (message: string) => ({
              roomType: 'All',
              date: 'All',
              error: message,
            })
          );
        }
      } else {
        result.updatedRates = rateUpdates.length;
      }

      // Update last sync time
      this.lastSyncTime.set(propertyId, new Date());

      return result;
    }, `Execute pricing strategy for property ${propertyId}`);
  }

  /**
   * Push MinLOS/MaxLOS restrictions to every active sibling rate plan for
   * each room — including derived rates (e.g. Non-Refundable) AND any
   * master parent rate (e.g. BAR) that isn't the configured primary
   * mapping. Restriction-only payload (no pricing) — WebHotelier rejects
   * pricing changes on derived rates, and the primary already received
   * its full pricing+restriction update via the main bridge push.
   * This is called as a separate step after the main pricing push.
   */
  private async pushRestrictionsToChildRates(
    propertyId: string,
    updatesWithRestrictions: Array<{
      roomTypeCode: string;
      date: Date;
      minLos?: number;
    }>
  ): Promise<void> {
    try {
      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        console.warn(
          '[PricingBridge] WebHotelier service not available for child rate restrictions'
        );
        return;
      }

      // Fetch all rates to identify child rate IDs per room
      const ratesResult = await webHotelierService.getRates();
      if (!ratesResult.success || !ratesResult.data) {
        console.warn(
          '[PricingBridge] Failed to fetch rates for child rate restriction push'
        );
        return;
      }

      const allRates = (ratesResult.data.rates || []) as unknown as Array<{
        id: number;
        room: string;
        active: number;
        parent: number;
      }>;

      // Build map: room code -> sibling rate IDs (every active rate plan for
      // the room that ISN'T the primary mapping). Previously this filtered to
      // `parent !== 0` (derived rates only), which misses the case where the
      // hotel's BAR is itself a master parent on a separate rate plan from
      // the configured primary. The edge function fan-out has always covered
      // both — this mirrors that so restrictions land on BAR even when the
      // edge function hasn't been redeployed yet.
      const primaryRateIds = new Set<number>();
      for (const m of this.roomTypeMappings.get(propertyId) || []) {
        if (typeof m.webHotelierRateId === 'number') {
          primaryRateIds.add(m.webHotelierRateId);
        }
      }

      const childRatesByRoom = new Map<string, number[]>();
      for (const r of allRates) {
        if (r.active === 1 && r.room && !primaryRateIds.has(r.id)) {
          if (!childRatesByRoom.has(r.room)) childRatesByRoom.set(r.room, []);
          childRatesByRoom.get(r.room)!.push(r.id);
        }
      }

      if (childRatesByRoom.size === 0) {
        console.log(
          '[PricingBridge] No sibling/derived rates found — skipping restriction push'
        );
        return;
      }

      console.log(
        '[PricingBridge] Sibling rates by room (incl. BAR):',
        Array.from(childRatesByRoom.entries()).map(([room, ids]) => ({
          room,
          siblingRateIds: ids,
        }))
      );

      // Build restriction updates for each child rate
      const restrictionUpdates: Array<{
        rateId: string;
        from: string;
        to: string;
        minStay?: number;
      }> = [];

      for (const update of updatesWithRestrictions) {
        const childRateIds = childRatesByRoom.get(update.roomTypeCode);
        if (!childRateIds || childRateIds.length === 0) continue;

        const dateStr = format(update.date, 'yyyy-MM-dd');
        // WebHotelier: min_stay=0 RESETS the per-day override (reverts to
        // baseline); 1+ SETS an override. Pushing 1 does NOT clear an existing
        // 2 — so map "no restriction" (<=1) to 0. This is what makes deleting a
        // MinLOS rule actually revert the sold (child) rates' min_stay.
        const apiMinStay =
          update.minLos && update.minLos > 1 ? update.minLos : 0;
        for (const childRateId of childRateIds) {
          restrictionUpdates.push({
            rateId: String(childRateId),
            from: dateStr,
            to: dateStr,
            minStay: apiMinStay,
          });
        }
      }

      if (restrictionUpdates.length === 0) {
        console.log(
          '[PricingBridge] No restriction updates to push to child rates'
        );
        return;
      }

      console.log(
        `[PricingBridge] Pushing min_stay restrictions to ${restrictionUpdates.length} child rate entries`
      );

      const result =
        await webHotelierService.updateRestrictions(restrictionUpdates);

      if (result.success) {
        console.log(
          '[PricingBridge] Successfully pushed restrictions to child rates'
        );
      } else {
        console.warn(
          '[PricingBridge] Failed to push restrictions to child rates:',
          result.error
        );
      }
    } catch (error) {
      console.warn(
        '[PricingBridge] Error pushing restrictions to child rates:',
        error
      );
    }
  }

  /**
   * Get room type mappings for a property
   */
  getRoomTypeMappings(propertyId: string): RoomTypeMapping[] {
    return this.roomTypeMappings.get(propertyId) || [];
  }

  /**
   * Get pricing configuration for a property
   */
  getPricingConfig(propertyId: string): DynamicPricingConfig | null {
    return this.pricingConfigs.get(propertyId) || null;
  }

  /**
   * Refresh property data from WebHotelier
   */
  async refreshPropertyData(
    propertyId: string
  ): Promise<ErrorHandlingResult<boolean>> {
    return handleDatabaseOperation(async () => {
      // Resolve property ID to database UUID if needed
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      // Clear cached data (use resolved UUID as cache key)
      this.roomTypeMappings.delete(resolvedPropertyId);
      this.pricingConfigs.delete(resolvedPropertyId);
      this.initializedProperties.delete(propertyId);
      this.lastSyncTime.delete(propertyId);

      // Reinitialize
      return await this.initializeProperty(propertyId);
    }, `Refresh property data for ${propertyId}`);
  }

  /**
   * Force reload pricing rules from database
   * Call this after creating/updating rules to ensure fresh data
   */
  async reloadPricingRules(propertyId: string): Promise<void> {
    const resolvedPropertyId = await this.resolvePropertyId(propertyId);
    console.log(
      `[PricingBridge] Force reloading pricing rules for ${resolvedPropertyId}`
    );
    await this.loadPricingConfig(resolvedPropertyId);
  }

  /**
   * Push MinLOS restrictions to child/derived rates (e.g. BAR) for all room types.
   * Call this after the edge function pricing push to ensure child rates get restrictions.
   * Loads active MinLOS rules, determines the restriction, and sends restriction-only
   * updates to all child rate plans via the WebHotelier API.
   *
   * Gap-fill override: if any active MinLOS rule has allowGapFillOverride=true,
   * dates inside an active reservation gap get min_stay=1 instead of the
   * rule-derived value. This mirrors the edge function logic — without it,
   * this function would overwrite the gap-aware value the edge function just
   * pushed to child rates.
   */
  async pushMinLosToChildRates(propertyId: string): Promise<void> {
    const resolvedPropertyId = await this.resolvePropertyId(propertyId);

    // Ensure pricing config is loaded (has MinLOS rules)
    let config = this.pricingConfigs.get(resolvedPropertyId);
    if (!config) {
      await this.loadPricingConfig(resolvedPropertyId);
      config = this.pricingConfigs.get(resolvedPropertyId);
    }

    const minLosRules = config?.minLosRules ?? [];
    const hasMinLosRules = minLosRules.length > 0;
    const gapOverrideEnabled = minLosRules.some((r) => r.gapOverride?.enabled);
    console.log(
      `[PricingBridge] MinLOS for child rates: ${minLosRules.length} active rule(s)${hasMinLosRules ? '' : ' (reset — no active rules)'}${gapOverrideEnabled ? ' (gap override enabled)' : ''}`
    );

    // Ensure room type mappings are loaded
    let mappings = this.roomTypeMappings.get(resolvedPropertyId);
    if (!mappings || mappings.length === 0) {
      await this.initializeProperty(propertyId);
      mappings = this.roomTypeMappings.get(resolvedPropertyId) || [];
    }

    if (mappings.length === 0) {
      console.warn(
        '[PricingBridge] No room type mappings — cannot push child rate restrictions'
      );
      return;
    }

    // If any rule has a gap override, load active gaps keyed by
    // "roomCode-date" with the gap length (nights). Per-room-type: a gap
    // relaxes min_stay ONLY for the room type that actually has the orphan
    // island — relaxing it on a room with open availability would defeat the
    // minimum-stay strategy. When several gaps cover the same room/date we keep
    // the SHORTEST (most likely inside the configured window, lowest-wins).
    const today = new Date();
    const gapNightsByRoomDate = new Map<string, number>();
    if (gapOverrideEnabled) {
      const todayStr = format(today, 'yyyy-MM-dd');
      const horizonStr = format(addDays(today, 365), 'yyyy-MM-dd');

      // gap.room_type_id is a UUID; mappings/min_stay keys are room codes.
      const { data: dbRoomTypes } = await supabase
        .from('room_types')
        .select('id, webhotelier_code')
        .eq('property_id', resolvedPropertyId)
        .not('webhotelier_code', 'is', null);
      const roomTypeIdToCode = new Map<string, string>(
        (dbRoomTypes || []).map((rt) => [rt.id, rt.webhotelier_code as string])
      );

      const { data: activeGaps, error: gapsError } = await supabase
        .from('reservation_gaps')
        .select('room_type_id, start_date, end_date, duration')
        .eq('property_id', resolvedPropertyId)
        .eq('status', 'identified')
        .gte('end_date', todayStr)
        .lte('start_date', horizonStr);
      if (gapsError) {
        console.warn(
          '[PricingBridge] Failed to load active gaps; min_stay relaxation skipped:',
          gapsError.message
        );
      } else if (activeGaps) {
        for (const gap of activeGaps) {
          const code = roomTypeIdToCode.get(gap.room_type_id);
          if (!code) continue;
          const gapStart = new Date(gap.start_date);
          const gapEnd = new Date(gap.end_date);
          for (
            let d = new Date(gapStart);
            d < gapEnd;
            d.setDate(d.getDate() + 1)
          ) {
            const key = `${code}-${format(d, 'yyyy-MM-dd')}`;
            const prev = gapNightsByRoomDate.get(key);
            gapNightsByRoomDate.set(
              key,
              prev == null ? gap.duration : Math.min(prev, gap.duration)
            );
          }
        }
        console.log(
          `[PricingBridge] Loaded ${activeGaps.length} active gaps spanning ${gapNightsByRoomDate.size} room/date entries for child rate min_stay relaxation`
        );
      }
    }

    // Build updates for all room types for 365 days. Per date we pick the
    // governing rule (most restrictive base minLos among rules applicable on
    // that date/room type) and let the shared resolver apply any override.
    const updates: Array<{ roomTypeCode: string; date: Date; minLos: number }> =
      [];
    let relaxedCount = 0;
    for (const mapping of mappings) {
      for (let i = 0; i < 365; i++) {
        const date = addDays(today, i);
        const dateStr = format(date, 'yyyy-MM-dd');

        // Applicability MUST mirror the edge function's parent-rate push:
        // scope, seasonal date range (valid_from/valid_until derived from
        // conditions.date_range at conversion) AND day-of-week. Without the
        // day/date gates a seasonal or weekend-only rule leaked onto child
        // rates for the whole year.
        const applicable = minLosRules.filter(
          (r) =>
            r.active &&
            this.shouldApplyRule(r, mapping.localRoomTypeId, date) &&
            (!r.dayOfWeek ||
              r.dayOfWeek.length === 0 ||
              r.dayOfWeek.includes(date.getDay()))
        );

        if (applicable.length === 0) {
          // No rule governs this date — reset restriction to 1 night.
          updates.push({
            roomTypeCode: mapping.webHotelierRoomCode,
            date,
            minLos: 1,
          });
          continue;
        }

        const governing = applicable.reduce((a, b) =>
          b.minLos > a.minLos ? b : a
        );

        const resolution = resolveEffectiveMinLos({
          baseMinLos: governing.minLos,
          gapOverride: governing.gapOverride,
          gapNights: gapNightsByRoomDate.get(
            `${mapping.localRoomTypeId}-${dateStr}`
          ),
          lastMinuteOverride: governing.lastMinuteOverride,
          daysAhead: i,
        });

        if (resolution.effectiveMinLos !== governing.minLos) relaxedCount++;
        updates.push({
          roomTypeCode: mapping.webHotelierRoomCode,
          date,
          minLos: resolution.effectiveMinLos,
        });
      }
    }

    console.log(
      `[PricingBridge] Pushing MinLOS to child rates for ${mappings.length} room types (${updates.length} total entries, ${relaxedCount} relaxed by gap-fill override)`
    );

    await this.pushRestrictionsToChildRates(resolvedPropertyId, updates);
  }

  /**
   * Get last synchronization time
   */
  getLastSyncTime(propertyId: string): Date | null {
    return this.lastSyncTime.get(propertyId) || null;
  }

  /**
   * Get the configured default/base prices for room types
   * First checks local database for user-configured defaults,
   * falls back to WebHotelier API if not configured.
   * Always ensures rate IDs are populated (fetches from API if missing in DB).
   */
  async getDefaultPrices(propertyId: string): Promise<
    Array<{
      roomTypeId: string;
      roomTypeName: string;
      basePrice: number;
      rateId: number;
    }>
  > {
    try {
      // Resolve property ID to database UUID if needed
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      // First, try to get defaults from local database
      const { data: localDefaults, error: dbError } = await supabase
        .from('room_type_defaults')
        .select(
          'room_type_code, room_type_name, default_price, webhotelier_rate_id'
        )
        .eq('property_id', resolvedPropertyId);

      if (!dbError && localDefaults && localDefaults.length > 0) {
        console.log(
          '[PricingBridge] Found local default prices:',
          localDefaults
        );

        // Check if any rate IDs are missing - if so, fetch them from WebHotelier
        const missingRateIds = localDefaults.some(
          (d) => !d.webhotelier_rate_id || d.webhotelier_rate_id === 0
        );

        if (missingRateIds) {
          console.log(
            '[PricingBridge] Some rate IDs missing in database, fetching from WebHotelier...'
          );
          const apiRates =
            await this.getDefaultPricesFromApi(resolvedPropertyId);
          const rateIdMap = new Map(
            apiRates.map((r) => [r.roomTypeId, r.rateId])
          );

          // Merge database defaults with API rate IDs
          const result = localDefaults.map((d) => {
            const dbRateId = d.webhotelier_rate_id;
            const apiRateId = rateIdMap.get(d.room_type_code) || 0;
            const rateId = dbRateId && dbRateId > 0 ? dbRateId : apiRateId;

            // If we found a rate ID from API that wasn't in DB, save it
            if (rateId > 0 && (!dbRateId || dbRateId === 0)) {
              console.log(
                `[PricingBridge] Updating missing rate ID for ${d.room_type_code}: ${rateId}`
              );
              // Fire-and-forget update to save rate ID
              supabase
                .from('room_type_defaults')
                .update({ webhotelier_rate_id: rateId })
                .eq('property_id', resolvedPropertyId)
                .eq('room_type_code', d.room_type_code)
                .then(({ error }) => {
                  if (error)
                    console.warn(
                      `[PricingBridge] Failed to update rate ID for ${d.room_type_code}:`,
                      error
                    );
                });
            }

            return {
              roomTypeId: d.room_type_code,
              roomTypeName: d.room_type_name || d.room_type_code,
              basePrice: Number(d.default_price),
              rateId,
            };
          });

          console.log('[PricingBridge] Merged defaults with rate IDs:', result);
          return result;
        }

        // All rate IDs present - return directly
        return localDefaults.map((d) => ({
          roomTypeId: d.room_type_code,
          roomTypeName: d.room_type_name || d.room_type_code,
          basePrice: Number(d.default_price),
          rateId: d.webhotelier_rate_id || 0,
        }));
      }

      console.log(
        '[PricingBridge] No local defaults found, fetching from WebHotelier...'
      );

      // Fall back to WebHotelier API
      return await this.getDefaultPricesFromApi(resolvedPropertyId);
    } catch (error) {
      console.error('[PricingBridge] Error getting default prices:', error);
      return [];
    }
  }

  /**
   * Get default prices from WebHotelier API (fallback)
   * Note: The 'initial' field in WebHotelier API doesn't represent true defaults,
   * it often just mirrors the current price. Use saveDefaultPrices to configure true defaults.
   */
  private async getDefaultPricesFromApi(propertyId?: string): Promise<
    Array<{
      roomTypeId: string;
      roomTypeName: string;
      basePrice: number;
      rateId: number;
    }>
  > {
    try {
      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        console.warn('[PricingBridge] WebHotelier service not available');
        return [];
      }

      // Get the rates to map room types to rate IDs
      const ratesResult = await webHotelierService.getRates();
      if (!ratesResult.success || !ratesResult.data) {
        console.warn(
          '[PricingBridge] Failed to fetch rates:',
          ratesResult.error
        );
        return [];
      }

      // Resolve room code -> ROOM TYPE name from room_types. getRates `name` is
      // the rate plan label (e.g. "BAR RO NR"), never the room type name.
      const nameByCode = new Map<string, string>();
      if (propertyId) {
        const { data: roomTypeRows } = await supabase
          .from('room_types')
          .select('code, name, webhotelier_code')
          .eq('property_id', propertyId);
        for (const rt of roomTypeRows || []) {
          if (rt.name) {
            if (rt.webhotelier_code)
              nameByCode.set(rt.webhotelier_code, rt.name);
            if (rt.code) nameByCode.set(rt.code, rt.name);
          }
        }
      }

      const rates = ratesResult.data.rates || [];
      const result: Array<{
        roomTypeId: string;
        roomTypeName: string;
        basePrice: number;
        rateId: number;
      }> = [];

      // Build result from parent rates only
      // WebHotelier API returns 'active', 'parent', 'room' fields that aren't in the TypeScript type
      const seenRoomTypes = new Set<string>();
      for (const rate of rates) {
        // Cast to access raw API fields
        const rawRate = rate as unknown as {
          active: number;
          parent: number;
          room: string;
          name?: string;
          id: number;
        };
        if (rawRate.active !== 1 || rawRate.parent !== 0) continue;

        const roomCode = rawRate.room;
        if (!roomCode || seenRoomTypes.has(roomCode)) continue;
        seenRoomTypes.add(roomCode);

        result.push({
          roomTypeId: roomCode,
          // Real room type name; never the rate-plan (BAR…) name.
          roomTypeName: nameByCode.get(roomCode) || roomCode,
          basePrice: 0, // No default configured - user needs to set it
          rateId: rawRate.id,
        });
      }

      console.log(
        '[PricingBridge] Room types from WebHotelier (no defaults configured):',
        result
      );
      return result;
    } catch (error) {
      console.error('[PricingBridge] Error fetching from WebHotelier:', error);
      return [];
    }
  }

  /**
   * Save default prices to local database
   * These are used for the "Revert to API Defaults" functionality
   */
  async saveDefaultPrices(
    propertyId: string,
    defaults: Array<{
      roomTypeId: string;
      roomTypeName: string;
      basePrice: number;
      rateId?: number;
    }>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      console.log('[PricingBridge] Saving default prices:', {
        propertyId: resolvedPropertyId,
        defaults,
      });

      // Upsert each default price
      for (const defaultPrice of defaults) {
        const { error } = await supabase.from('room_type_defaults').upsert(
          {
            property_id: resolvedPropertyId,
            room_type_code: defaultPrice.roomTypeId,
            room_type_name: defaultPrice.roomTypeName,
            default_price: defaultPrice.basePrice,
            webhotelier_rate_id: defaultPrice.rateId || null,
          },
          {
            onConflict: 'property_id,room_type_code',
          }
        );

        if (error) {
          console.error('[PricingBridge] Error saving default price:', error);
          return { success: false, error: error.message };
        }
      }

      console.log('[PricingBridge] Default prices saved successfully');
      return { success: true };
    } catch (error) {
      console.error('[PricingBridge] Error saving default prices:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Save the authoritative per-date base prices (seasonal ranges) and seed
   * calendar_data.base_rate from them immediately.
   *
   * This is the independent base source: base prices live in base_price_periods,
   * NOT in the live WebHotelier channel that pricing rules mutate. base_rate is
   * derived from here, so an applied rule can never become the new "base" and
   * reverts always restore the true baseline. The sync worker also reads these
   * periods, so future/new dates get the correct seasonal base too.
   */
  async saveBasePricePeriods(
    propertyId: string,
    periods: Array<{
      roomTypeCode: string;
      startDate: string; // yyyy-MM-dd
      endDate: string; // yyyy-MM-dd
      basePrice: number;
      rateId?: number;
    }>
  ): Promise<{
    success: boolean;
    error?: string;
    calendarRowsUpdated: number;
  }> {
    try {
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);

      const validPeriods = periods.filter(
        (p) => p.basePrice > 0 && p.startDate <= p.endDate
      );
      if (validPeriods.length === 0) {
        return { success: true, calendarRowsUpdated: 0 };
      }

      const { error: upsertError } = await supabase
        .from('base_price_periods')
        .upsert(
          validPeriods.map((p) => ({
            property_id: resolvedPropertyId,
            room_type_code: p.roomTypeCode,
            start_date: p.startDate,
            end_date: p.endDate,
            base_price: p.basePrice,
            webhotelier_rate_id: p.rateId ?? null,
            updated_at: new Date().toISOString(),
          })),
          { onConflict: 'property_id,room_type_code,start_date' }
        );

      if (upsertError) {
        console.error(
          '[PricingBridge] Error saving base price periods:',
          upsertError
        );
        return {
          success: false,
          error: upsertError.message,
          calendarRowsUpdated: 0,
        };
      }

      // Seed calendar_data.base_rate from the periods now (don't wait for sync).
      // Only base_rate is written — final_rate is owned by the pricing engine.
      let calendarRowsUpdated = 0;
      for (const p of validPeriods) {
        const { error: calError, count } = await supabase
          .from('calendar_data')
          .update({ base_rate: p.basePrice }, { count: 'exact' })
          .eq('property_id', resolvedPropertyId)
          .eq('room_type_code', p.roomTypeCode)
          .gte('date', p.startDate)
          .lte('date', p.endDate);
        if (calError) {
          console.warn(
            `[PricingBridge] base_rate seed failed for ${p.roomTypeCode} ${p.startDate}..${p.endDate}:`,
            calError.message
          );
        } else {
          calendarRowsUpdated += count ?? 0;
        }
      }

      console.log(
        `[PricingBridge] Saved ${validPeriods.length} base price periods, seeded ${calendarRowsUpdated} calendar rows`
      );
      return { success: true, calendarRowsUpdated };
    } catch (error) {
      console.error('[PricingBridge] Error saving base price periods:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        calendarRowsUpdated: 0,
      };
    }
  }

  /**
   * Sync prices to WebHotelier API
   * Note: WebHotelier is the source of truth for prices, so this is a no-op.
   * Price updates are pushed through the pricing rules engine, not manual syncs.
   */
  async syncPricesToWebHotelier(
    propertyId: string
  ): Promise<ErrorHandlingResult<boolean>> {
    return handleDatabaseOperation(async () => {
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);
      this.lastSyncTime.set(resolvedPropertyId, new Date());
      return true;
    }, `Sync prices to WebHotelier for ${propertyId}`);
  }

  /**
   * Get current live prices from WebHotelier API
   */
  async getCurrentPrices(
    propertyId: string
  ): Promise<
    Array<{ roomTypeId: string; roomTypeName: string; currentPrice: number }>
  > {
    try {
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);
      const mappings = this.roomTypeMappings.get(resolvedPropertyId) || [];

      // Return current prices from mappings
      return mappings.map((m) => ({
        roomTypeId: m.localRoomTypeId,
        roomTypeName: m.name,
        currentPrice: m.currentPrice,
      }));
    } catch (error) {
      console.error(
        '[WebHotelierPricingBridge] Error getting current prices:',
        error
      );
      return [];
    }
  }

  /**
   * Revert prices to API defaults by pushing base prices back to WebHotelier
   * Fetches the ACTUAL default prices from WebHotelier API and pushes them for the date range
   * @param propertyId - Property ID
   * @param roomTypeId - Optional room type ID to revert (if not provided, reverts all)
   * @param dateRange - Optional date range (defaults to next 31 days)
   */
  async revertToDefaultPrices(
    propertyId: string,
    roomTypeId?: string,
    dateRange?: { startDate: Date; endDate: Date }
  ): Promise<ErrorHandlingResult<{ updated: number; failed: number }>> {
    return handleDatabaseOperation(async () => {
      const rateUpdateService = this.getRateUpdateService();
      if (!rateUpdateService) {
        throw new Error('Rate update service not available');
      }

      const webHotelierService = this.getWebHotelierService();
      if (!webHotelierService) {
        throw new Error('WebHotelier service not available');
      }

      // Get property code from service config
      const propertyInfo = await webHotelierService.getPropertyInfo();
      if (!propertyInfo.success || !propertyInfo.data) {
        throw new Error('Failed to get property info');
      }
      const propertyCode = propertyInfo.data.code;

      // Fetch the ACTUAL default prices from WebHotelier API
      const apiDefaults = await this.getDefaultPrices(propertyId);
      console.log('[PricingBridge] API defaults for revert:', apiDefaults);

      if (apiDefaults.length === 0) {
        console.warn(
          '[PricingBridge] No default prices found from WebHotelier API'
        );
        return { updated: 0, failed: 0 };
      }

      // Filter to specific room type if provided
      const targetDefaults = roomTypeId
        ? apiDefaults.filter((d) => d.roomTypeId === roomTypeId)
        : apiDefaults;

      if (targetDefaults.length === 0) {
        console.warn('[PricingBridge] No matching room types found for revert');
        return { updated: 0, failed: 0 };
      }

      // Default date range: today + 31 days
      const startDate = dateRange?.startDate || new Date();
      const endDate =
        dateRange?.endDate || new Date(Date.now() + 31 * 24 * 60 * 60 * 1000);

      // Build rate updates for each room type and each date in the range
      const updates: Array<{
        roomTypeCode: string;
        webHotelierRateId?: number;
        date: Date;
        price: number;
      }> = [];

      for (const defaultData of targetDefaults) {
        const defaultPrice = defaultData.basePrice;

        if (!defaultPrice || defaultPrice <= 0) {
          console.warn(
            `[PricingBridge] No valid base price for ${defaultData.roomTypeId}, skipping`
          );
          continue;
        }

        // Generate updates for each date in the range
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          updates.push({
            roomTypeCode: defaultData.roomTypeId,
            webHotelierRateId: defaultData.rateId,
            date: new Date(currentDate),
            price: defaultPrice,
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      if (updates.length === 0) {
        console.log('[PricingBridge] No updates to send');
        return { updated: 0, failed: 0 };
      }

      console.log(
        `[PricingBridge] Reverting ${updates.length} price entries to WebHotelier API defaults`
      );
      console.log(
        `[PricingBridge] Date range: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`
      );
      console.log(
        `[PricingBridge] Room types: ${targetDefaults.map((d) => `${d.roomTypeId} @ €${d.basePrice} (Rate ID: ${d.rateId})`).join(', ')}`
      );

      // Push updates to WebHotelier with force=true to bypass all filters
      const result = await rateUpdateService.syncCalculatedPrices(
        propertyCode,
        updates.map((u) => {
          const update: {
            roomTypeCode: string;
            webHotelierRateId?: number;
            date: Date;
            calculatedPrice: number;
            originalPrice: number;
            confidence: number;
          } = {
            roomTypeCode: u.roomTypeCode,
            date: u.date,
            calculatedPrice: u.price,
            originalPrice: u.price, // Same as calculated since we're reverting
            confidence: 1.0, // High confidence for defaults
          };
          // Only include webHotelierRateId if it has a value (exactOptionalPropertyTypes compliance)
          if (u.webHotelierRateId != null && u.webHotelierRateId > 0) {
            update.webHotelierRateId = u.webHotelierRateId;
          }
          return update;
        }),
        {
          minConfidence: 0, // Accept all updates
          maxPriceChange: 1.0, // Allow any price change
          dryRun: false,
          force: true, // Force push all prices, bypassing minimal change filter
        }
      );

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update rates');
      }

      console.log(
        `[PricingBridge] Successfully reverted prices: ${result.data?.updated || updates.length} updated, ${result.data?.failed || 0} failed`
      );

      // Also update calendar_data table with the default prices
      const resolvedPropertyId = await this.resolvePropertyId(propertyId);
      console.log(
        `[PricingBridge] Updating calendar_data for property ${resolvedPropertyId}`
      );

      for (const defaultData of targetDefaults) {
        const defaultPrice = defaultData.basePrice;
        if (!defaultPrice || defaultPrice <= 0) continue;

        // Update all dates in range for this room type
        const { error: calendarError } = await supabase
          .from('calendar_data')
          .update({
            base_rate: defaultPrice,
            final_rate: defaultPrice,
          })
          .eq('property_id', resolvedPropertyId)
          .eq('room_type_code', defaultData.roomTypeId)
          .gte('date', format(startDate, 'yyyy-MM-dd'))
          .lte('date', format(endDate, 'yyyy-MM-dd'));

        if (calendarError) {
          console.warn(
            `[PricingBridge] Failed to update calendar_data for ${defaultData.roomTypeId}:`,
            calendarError
          );
        } else {
          console.log(
            `[PricingBridge] Updated calendar_data for ${defaultData.roomTypeId} to €${defaultPrice}`
          );
        }
      }

      return {
        updated: result.data?.updated || updates.length,
        failed: result.data?.failed || 0,
      };
    }, `Revert prices to defaults for ${propertyId}`);
  }

  /**
   * Execute pricing strategy in batches for large date ranges.
   * Splits date ranges larger than BATCH_SIZE into sequential batches.
   * Useful for year-long pricing operations.
   */
  async executePricingStrategyBatched(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    options: {
      dryRun?: boolean;
      roomTypeId?: string;
      minConfidence?: number;
      maxPriceChange?: number;
      onProgress?: (
        completed: number,
        total: number,
        currentBatch: string
      ) => void;
    } = {}
  ): Promise<ErrorHandlingResult<PricingExecutionResult>> {
    return handleDatabaseOperation(async () => {
      const totalDays = differenceInDays(endDate, startDate);
      const batchSize = PRICING_CONFIG.BATCH_SIZE;

      // If range is small enough, use regular execution
      if (totalDays <= batchSize) {
        const result = await this.executePricingStrategy(
          propertyId,
          startDate,
          endDate,
          options
        );
        return result.success
          ? result.data!
          : (() => {
              throw new Error(result.error?.message || 'Execution failed');
            })();
      }

      // Calculate number of batches
      const numBatches = Math.ceil(totalDays / batchSize);
      console.log(
        `[PricingBridge] Batched execution: ${totalDays} days in ${numBatches} batches of ${batchSize} days`
      );

      const aggregatedResult: PricingExecutionResult = {
        success: true,
        propertyCode: '',
        processedRooms: 0,
        updatedRates: 0,
        errors: [],
        priceChanges: [],
      };

      // Process batches sequentially to avoid rate limits
      for (let i = 0; i < numBatches; i++) {
        const batchStart = addDays(startDate, i * batchSize);
        const batchEnd = addDays(
          batchStart,
          Math.min(batchSize, differenceInDays(endDate, batchStart))
        );

        const batchLabel = `${format(batchStart, 'MMM d')} - ${format(batchEnd, 'MMM d')}`;
        console.log(
          `[PricingBridge] Processing batch ${i + 1}/${numBatches}: ${batchLabel}`
        );

        // Notify progress
        options.onProgress?.(i, numBatches, batchLabel);

        const batchResult = await this.executePricingStrategy(
          propertyId,
          batchStart,
          batchEnd,
          {
            dryRun: options.dryRun,
            roomTypeId: options.roomTypeId,
            minConfidence: options.minConfidence,
            maxPriceChange: options.maxPriceChange,
          }
        );

        if (batchResult.success && batchResult.data) {
          // Aggregate results
          if (!aggregatedResult.propertyCode) {
            aggregatedResult.propertyCode = batchResult.data.propertyCode;
          }
          aggregatedResult.processedRooms += batchResult.data.processedRooms;
          aggregatedResult.updatedRates += batchResult.data.updatedRates;
          aggregatedResult.errors.push(...batchResult.data.errors);
          aggregatedResult.priceChanges.push(...batchResult.data.priceChanges);
        } else {
          // Log batch failure but continue with other batches
          console.error(
            `[PricingBridge] Batch ${i + 1} failed:`,
            batchResult.error
          );
          aggregatedResult.errors.push({
            roomType: 'batch',
            date: batchLabel,
            error: batchResult.error?.message || 'Batch execution failed',
          });
        }

        // Small delay between batches to avoid overwhelming the API
        if (i < numBatches - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      // Final progress notification
      options.onProgress?.(numBatches, numBatches, 'Complete');

      // Mark as failed if any errors occurred
      if (aggregatedResult.errors.length > 0) {
        aggregatedResult.success = aggregatedResult.updatedRates > 0; // Partial success if some rates updated
      }

      console.log(
        `[PricingBridge] Batched execution complete: ${aggregatedResult.updatedRates} rates updated, ${aggregatedResult.errors.length} errors`
      );

      return aggregatedResult;
    }, `Batched pricing strategy for ${propertyId}`);
  }
}

// Export singleton
let webHotelierPricingBridge: WebHotelierPricingBridge | null = null;

registerResetHandler(() => {
  webHotelierPricingBridge = null;
});

export function getWebHotelierPricingBridge(): WebHotelierPricingBridge {
  // Return cached instance immediately if available
  if (webHotelierPricingBridge) {
    return webHotelierPricingBridge;
  }

  try {
    console.log('[WebHotelier Pricing Bridge] Creating new instance');
    webHotelierPricingBridge = new WebHotelierPricingBridge();
    console.log('[WebHotelier Pricing Bridge] Instance created successfully');
    return webHotelierPricingBridge;
  } catch (error) {
    console.error(
      '[WebHotelier Pricing Bridge] Error creating instance:',
      error
    );
    throw new Error('Failed to create WebHotelier Pricing Bridge instance');
  }
}

export default WebHotelierPricingBridge;
